import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import './PaymentModal.css';
import customFetch from '../apicall/api';
import { generateId } from '../Utility';
import moment from 'moment';
import toast, { Toaster } from 'react-hot-toast';
import PayButtonIcon from '../../assets/images/pay-button.svg';

/**
 * PaymentModal Component - Modified for Razorpay Payment Flow
 * 
 * New Structure:
 * 1. Search Modes: Loan ID, Mobile Number, Aadhaar, PAN Card
 * 2. Search functionality from Payments.js
 * 3. Display: User details + Collection table
 * 4. Actions: Clear button (reset) + Pay Now button (Razorpay)
 */

export default function PaymentModal({ items: initialItems, onClose, api_token, onDataRefresh, isOpen, householdNumber }) {
  
  const history = useHistory();

  // Get API token from props or localStorage
  const apiToken = api_token || localStorage.getItem('api_token');

  // NEW: Search mode states
  const [searchMode, setSearchMode] = useState('loanid'); // 'loanid', 'mobile', 'aadhaar', 'pan'
  const [searchInput, setSearchInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [ishhdata, setIshhdata] = useState(false); // Conditional endpoint flag
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 769);
  const [isSearchInputDisabled, setIsSearchInputDisabled] = useState(false);
  const [isCollectionConfirmed, setIsCollectionConfirmed] = useState(false);
  
  // NEW: Household data from search
  const [items, setItems] = useState(initialItems || null);
  const [showDataCard, setShowDataCard] = useState(!!initialItems);
  const [isFromURLParam, setIsFromURLParam] = useState(false);
  const [urlParamAmount, setUrlParamAmount] = useState(0);
  const [isLoadedFromURLParam, setIsLoadedFromURLParam] = useState(!!householdNumber); // Initialize based on householdNumber prop
  const [isInitialized, setIsInitialized] = useState(!householdNumber); // If no householdNumber, we're ready immediately

  // Extract values from items (only when items exist)
  const overdueDemand = items ? parseFloat(Math.max(0, parseFloat(items.tot_overdue || 0)).toFixed(2)) : 0;
  const currentDemand = items ? parseFloat(Math.max(0, parseFloat((items.Balance || 0) - (items.tot_overdue || 0))).toFixed(2)) : 0;
  const chargeDue = items ? parseFloat(Math.max(0, parseFloat(items.Charges_os || 0)).toFixed(2)) : 0;
  const collectedAmount = items ? parseFloat(parseFloat(items.Collected || 0).toFixed(2)) : 0;
  const standardEmi = items ? parseFloat(parseFloat(items.emi || 0).toFixed(2)) : 0;
  const loanOutstanding = items ? parseFloat(parseFloat(items.Loan_os || 0).toFixed(2)) : 0;
  const totalDemandValue = items ? parseFloat(parseFloat(items.Demand || 0).toFixed(2)) : 0;
  
  const advancePaid = parseFloat(Math.max(0, collectedAmount - totalDemandValue).toFixed(2));
  
  const scenario1 = standardEmi;
  const scenario2 = parseFloat(Math.max(0, loanOutstanding - totalDemandValue).toFixed(2));
  const scenario3 = parseFloat(Math.max(0, standardEmi - advancePaid).toFixed(2));
  const emiAmount = parseFloat(Math.max(0, Math.min(scenario1, scenario2, scenario3)).toFixed(2));
  
  const totalDemand = items ? parseFloat((Math.max(0, parseFloat(items.Balance || 0)) + Math.max(0, parseFloat(items.Charges_os || 0))).toFixed(2)) : 0;
  
  const [maxPrepaymentEmis, setMaxPrepaymentEmis] = useState(2);
  const [nachBounceCharge, setNachBounceCharge] = useState(0);
  
  const prepaymentScenario1 = standardEmi * maxPrepaymentEmis;
  const prepaymentScenario2 = Math.max(0, loanOutstanding - totalDemandValue - standardEmi);
  const prepaymentScenario3 = Math.max(0, standardEmi * (maxPrepaymentEmis + 1) - advancePaid);
  const maxPrepaymentAmount = Math.max(0, Math.min(prepaymentScenario1, prepaymentScenario2, prepaymentScenario3));

  // Collection states
  const [totalCollection, setTotalCollection] = useState(0);
  const [odCollection, setOdCollection] = useState(0);
  const [currentCollection, setCurrentCollection] = useState(0);
  const [chargeCollection, setChargeCollection] = useState(0);
  const [advanceEmiCollection, setAdvanceEmiCollection] = useState(0);
  const [prepaymentCollection, setPrepaymentCollection] = useState(0);
  const [foreclosureCollection, setForeclosureCollection] = useState(0);
  const [foreclosureCharges, setForeclosureCharges] = useState(0);
  const [netForeclosureAmount, setNetForeclosureAmount] = useState(0);
  const [showForeclosureCharges, setShowForeclosureCharges] = useState(true);

  // Tooltip states
  const [showPrepaymentTooltip, setShowPrepaymentTooltip] = useState(false);
  const [showForeclosureTooltip, setShowForeclosureTooltip] = useState(false);

  // Display states
  const [totalCollectionDisplay, setTotalCollectionDisplay] = useState('');
  const [chargeCollectionDisplay, setChargeCollectionDisplay] = useState('');
  const [advanceEmiDisplay, setAdvanceEmiDisplay] = useState('');
  const [prepaymentDisplay, setPrepaymentDisplay] = useState('');
  const [foreclosureDisplay, setForeclosureDisplay] = useState('');

  // Remaining demand tracking
  const [remainingDemand, setRemainingDemand] = useState({
    od: 0,
    current: 0,
    charges: 0,
    nextEmi: 0,
    total: 0
  });

  // Other states
  const [manuallyEditedFields, setManuallyEditedFields] = useState(new Set());
  const [autoAllocatedFields, setAutoAllocatedFields] = useState(new Set());
  const isChargeTransferRef = useRef(false);
  const isNextEmiFromTransferRef = useRef(false);
  const previousChargeAmountRef = useRef(0);
  const previousNextEmiAmountRef = useRef(0);
  const [hasShownOverflowMessage, setHasShownOverflowMessage] = useState(false);
  const [isUpdatingFromManualFields, setIsUpdatingFromManualFields] = useState(false);
  const isManualUpdateRef = useRef(false);
  const [hasUserEnteredTotal, setHasUserEnteredTotal] = useState(false);
  const [originalAllocation, setOriginalAllocation] = useState({
    od: 0,
    current: 0,
    charges: 0,
    nextEmi: 0,
    prepayment: 0
  });

  // Razorpay payment states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Confirmation modal states
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMode, setConfirmationMode] = useState('');
  const [collectionMode, setCollectionMode] = useState('Razorpay'); // Payment collection mode

  // Function to perform search from URL parameter (defined before useEffect)
  const performURLSearch = async (householdNumber, useLatestOutstanding = false) => {
    // Validate Loan ID format
    const loanIdRegex = /^[A-Z]{2}\d{3}[A-Z]{2}\d{6}(N\d{2})?$/;
    if (!loanIdRegex.test(householdNumber)) {
      setSearchError('Invalid Loan ID format');
      return;
    }

    // Reset table data immediately when search starts
    resetCollectionFields();
    setTotalCollection(0);
    setTotalCollectionDisplay('');
    
    setIsSearching(true);
    setSearchError('');

    try {
      // STEP 1: Call _latest_outstanding_amount to get payment allocation (Payment column)
      const outstandingResponse = await customFetch(
        `${process.env.REACT_APP_CLOUD}/${process.env.REACT_APP_ENVIRONMENT}_latest_outstanding_amount`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            hhnumber: householdNumber.toString(),
          }),
        }
      );

      const outstandingData = await outstandingResponse.json();

      // Check for errors in first API
      if (outstandingData.response_code === 'no_bill_found' && outstandingData.status === 'failed') {
        setSearchError('No payment allocation found');
        setShowDataCard(false);
        setItems(null);
        setIsInitialized(true);
        setIsSearching(false);
        return;
      } else if (outstandingData.error) {
        setSearchError(outstandingData.error);
        setShowDataCard(false);
        setItems(null);
        setIsInitialized(true);
        setIsSearching(false);
        return;
      }

      // STEP 2: Call _latest_household_payment to get demand data (Due column)
      const householdPaymentResponse = await customFetch(
        `${process.env.REACT_APP_CLOUD}/${process.env.REACT_APP_ENVIRONMENT}_latest_household_payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            hhnumber: householdNumber.toString(),
          }),
        }
      );

      const householdData = await householdPaymentResponse.json();

      // Check for errors in second API
      if (householdData.response_code === 'no_bill_found' && householdData.status === 'failed') {
        setSearchError('No bill found');
        setShowDataCard(false);
        setItems(null);
        setIsInitialized(true);
        setIsSearching(false);
        return;
      } else if (householdData.error) {
        setSearchError(householdData.error);
        setShowDataCard(false);
        setItems(null);
        setIsInitialized(true);
        setIsSearching(false);
        return;
      }

      // Set the household data from second API (Due column)
      // Use csf from _latest_outstanding_amount API (outstandingData) instead of _latest_household_payment
      setItems({
        Lead_Name: householdData.name,
        HH_ID: householdData.hhnumber,
        Household_mobile_no: householdData.mobile_no,
        aadhaar_no: householdData.adhar_no || householdData.aadhaar_no,
        pan_no: householdData.pan_no,
        Balance: parseFloat(householdData.balance || householdData.amount || 0),
        tot_overdue: parseFloat(householdData.tot_overdue || 0),
        Charges_os: parseFloat(householdData.charges_os || householdData.Charges_os || 0),
        emi: parseFloat(householdData.emi || 0),
        Loan_os: parseFloat(householdData.loan_os || householdData.Loan_os || 0),
        Demand: parseFloat(householdData.demand || householdData.Demand || householdData.balance || householdData.amount || 0),
        Collected: parseFloat(householdData.collection || householdData.Collected || 0),
        disbursement_amt: householdData.disbursement_amt,
        csf: outstandingData.csf,
        row_id: householdData.row_id,
        household_id: householdData.hhnumber,
      });

      // Set the payment allocation from second API (Payment column)
      const totalAmount = parseFloat(outstandingData.amount || 0);
      const odAmount = parseFloat(outstandingData.odCollection || 0);
      const currentAmount = parseFloat(outstandingData.currentCollection || 0);
      const chargeAmount = parseFloat(outstandingData.chargeCollection || 0);
      const advanceAmount = parseFloat(outstandingData.advanceEmiCollection || 0);
      const prepaymentAmount = parseFloat(outstandingData.prepaymentCollection || 0);
      const foreclosureAmount = parseFloat(outstandingData.foreclosureCollection || 0);
      const foreclosureChargesAmount = parseFloat(outstandingData.foreclosureCharges || 0);
      const prepaymentCount = parseInt(outstandingData.prepaymentCount || 0);

      // Populate Payment column with data from second API
      setTotalCollection(totalAmount);
      setTotalCollectionDisplay(totalAmount.toString());
      setOdCollection(odAmount);
      setCurrentCollection(currentAmount);
      setChargeCollection(chargeAmount);
      setChargeCollectionDisplay(chargeAmount > 0 ? chargeAmount.toString() : '');
      setAdvanceEmiCollection(advanceAmount);
      setAdvanceEmiDisplay(advanceAmount > 0 ? advanceAmount.toString() : '');
      setPrepaymentCollection(prepaymentAmount);
      setPrepaymentDisplay(prepaymentAmount > 0 ? prepaymentAmount.toString() : '');
      setForeclosureCollection(foreclosureAmount);
      setForeclosureDisplay(foreclosureAmount > 0 ? foreclosureAmount.toString() : '');
      setForeclosureCharges(foreclosureChargesAmount);
      setNetForeclosureAmount(foreclosureAmount); // Net foreclosure is the main amount
      setShowForeclosureCharges(foreclosureChargesAmount > 0);
      
      if (prepaymentCount > 0) {
        setMaxPrepaymentEmis(prepaymentCount);
      }

      setSearchError('');
      setIsFromURLParam(true); // Mark as URL parameter search
      setIsLoadedFromURLParam(true); // Persistent flag for disabling buttons
      setIsCollectionConfirmed(true); // Start with fields disabled
      setIsInitialized(true); // Mark as initialized after data is loaded
      setShowDataCard(true); // Show the data card

    } catch (error) {
      console.error('URL Search error:', error);
      setSearchError('Failed to search. Please try again.');
      setShowDataCard(false);
      setItems(null);
      setIsInitialized(true); // Mark as initialized even on error
    } finally {
      setIsSearching(false);
    }
  };

  // Detect screen size for conditional styling
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 769);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // DevTools protection - Keyboard shortcuts and right-click disabled (no dimension detection for iframe compatibility)
  useEffect(() => {
    // if (!isLoadedFromURLParam) return;

    // Prevent keyboard shortcuts
    const handleKeyDown = (e) => {
      // F12
      if (e.keyCode === 123) {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+I (DevTools)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
        e.preventDefault();
        return false;
      }
      // Ctrl+Shift+C (Inspect)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 67) {
        e.preventDefault();
        return false;
      }
      // Ctrl+U (View Source)
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
        return false;
      }
      // Cmd+Option+I (Mac DevTools)
      if (e.metaKey && e.altKey && e.keyCode === 73) {
        e.preventDefault();
        return false;
      }
      // Cmd+Option+J (Mac Console)
      if (e.metaKey && e.altKey && e.keyCode === 74) {
        e.preventDefault();
        return false;
      }
      // Cmd+Option+C (Mac Inspect)
      if (e.metaKey && e.altKey && e.keyCode === 67) {
        e.preventDefault();
        return false;
      }
    };

    // Prevent right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [isLoadedFromURLParam]);

  // Check for household number prop from parent component (Payments.js)
  useEffect(() => {
    if (householdNumber) {
      console.log('household_number from props:', householdNumber);
      
      // Set search mode to Loan ID and input value
      setSearchMode('loanid');
      setSearchInput(householdNumber);
      
      // Set ishhdata to true for URL parameter searches (use _latest_outstanding_amount endpoint)
      setIshhdata(true);
      
      // Perform search with the household number using _latest_outstanding_amount endpoint
      performURLSearch(householdNumber, true);
    } else {
      // No household number, mark as initialized
      setIsInitialized(true);
    }
  }, [householdNumber]); // Run when householdNumber prop changes

  // Fetch prepayment configuration from API when modal is opened
  useEffect(() => {
    if (!isOpen || !items) {
      return;
    }

    const fetchPrepaymentConfig = async () => {
      try {
        const payload = {
          project_name: "Prepayment_Emi",
          decision_name: "SBL_PREPAYMENT_EMI_CONFIG",
          context: {
            branch_id: items.branch_id || 5,
            product_id: items.product_id || 4,
            tenure: items.tenure || 0,
            txn_days: items.txn_days || 30,
            remaining_current_demand_emi: items.remaining_current_demand_emi || 1,
            nach_flag: items.nach_flag || "F"
          },
          trace: false,
          sample_trace: false
        };

        const response = await customFetch(
          `${process.env.REACT_APP_CLOUD}/${process.env.REACT_APP_ENVIRONMENT}_evaluate_business_rule`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'api-token': apiToken,
            },
            body: JSON.stringify(payload)
          }
        );

        const data = await response.json();
        
        if (data.status === 'SUCCESS' && data.result && data.result.max_prepayment_emis !== undefined) {
          const maxEmis = parseInt(data.result.max_prepayment_emis);
          setMaxPrepaymentEmis(maxEmis);
        } else {
          setMaxPrepaymentEmis(2);
        }
      } catch (error) {
        console.error('Error fetching prepayment config:', error);
        setMaxPrepaymentEmis(2);
      }
    };

    fetchPrepaymentConfig();
  }, [isOpen, items]);

  // Fetch NACH bounce charges from API when modal is opened
  useEffect(() => {
    if (!isOpen || !items) {
      return;
    }

    const fetchBounceCharge = async () => {
      try {
        const payload = {
          project_name: "Bounce_Charge",
          decision_name: "SBL_NACH_BOUNCE_CHARGES",
          context: {
            branch_id: items.branch_id || 5,
            product_id: items.product_id || 4,
            tenure: items.tenure || 0,
            txn_days: items.txn_days || 30,
            remaining_current_demand_emi: items.remaining_current_demand_emi || 1,
            nach_flag: items.nach_flag || "F",
            bounce_flag: items.bounce_flag || 1
          },
          trace: false,
          sample_trace: false
        };

        const response = await customFetch(
          `${process.env.REACT_APP_CLOUD}/${process.env.REACT_APP_ENVIRONMENT}_evaluate_business_rule`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'api-token': apiToken,
            },
            body: JSON.stringify(payload)
          }
        );

        const data = await response.json();
        
        if (data.status === 'SUCCESS' && data.result && data.result.nach_bounce_charge !== undefined) {
          const bounceCharge = parseFloat(parseFloat(data.result.nach_bounce_charge).toFixed(2));
          setNachBounceCharge(bounceCharge);
        } else {
          setNachBounceCharge(0);
        }
      } catch (error) {
        console.error('Error fetching bounce charge:', error);
        setNachBounceCharge(0);
      }
    };

    fetchBounceCharge();
  }, [isOpen, items]);

  // Initialize remaining demand when items are loaded
  useEffect(() => {
    if (items) {
      setRemainingDemand({
        od: parseFloat(overdueDemand.toFixed(2)),
        current: parseFloat(currentDemand.toFixed(2)),
        charges: parseFloat((chargeDue + nachBounceCharge).toFixed(2)),
        nextEmi: parseFloat(emiAmount.toFixed(2)),
        total: parseFloat(totalDemand.toFixed(2))
      });
      setShowDataCard(true);
      setIsSearchInputDisabled(true);
    }
  }, [items, overdueDemand, currentDemand, chargeDue, emiAmount, totalDemand, nachBounceCharge]);

  // NOTE: Auto-allocation removed for URL parameters - payment data comes directly from API
  // useEffect(() => {
  //   if (isFromURLParam && urlParamAmount > 0 && remainingDemand.total > 0) {
  //     autoAllocateCollection(urlParamAmount, false);
  //     setUrlParamAmount(0);
  //     setIsFromURLParam(false);
  //   }
  // }, [isFromURLParam, urlParamAmount, remainingDemand]);

  // Validation functions
  const validateLoanId = (value) => {
    // MP001LE000232 or MP001LE000232N01 format (accepts lowercase too)
    const loanIdRegex = /^[A-Za-z]{2}\d{3}[A-Za-z]{2}\d{6}(N\d{2})?$/i;
    return loanIdRegex.test(value);
  };

  const validateMobile = (value) => {
    // Must be exactly 10 digits
    if (!/^\d{10}$/.test(value)) {
      return false;
    }
    
    // Must start with 6, 7, 8, or 9
    if (!/^[6-9]/.test(value)) {
      return false;
    }
    
    // Check for straight sequence (1234567890)
    if (value === '1234567890') {
      return false;
    }
    
    // Check for all same digits (0000000000, 1111111111, etc.)
    if (/^(.)\1{9}$/.test(value)) {
      return false;
    }
    
    // Check for known test/dummy numbers
    const dummyNumbers = ['8888888888', '9191919191', '1231231234'];
    if (dummyNumbers.includes(value)) {
      return false;
    }
    
    return true;
  };

  const validateAadhaar = (value) => {
    return /^\d{12}$/.test(value);
  };

  const validatePAN = (value) => {
    return /^[A-Z]{5}\d{4}[A-Z]{1}$/.test(value.toUpperCase());
  };

  const getValidationMessage = () => {
    switch(searchMode) {
      case 'loanid':
        return 'Enter valid Loan ID (e.g., MP001LE000232 or MP001LE000232N01)';
      case 'mobile':
        return 'Enter valid 10-digit mobile number starting with 6, 7, 8, or 9';
      case 'aadhaar':
        return 'Enter valid 12-digit Aadhaar number';
      case 'pan':
        return 'Enter valid PAN card (e.g., ABCDE1234F)';
      default:
        return '';
    }
  };

  const isValidInput = () => {
    if (!searchInput) return false;
    
    switch(searchMode) {
      case 'loanid':
        return validateLoanId(searchInput);
      case 'mobile':
        return validateMobile(searchInput);
      case 'aadhaar':
        return validateAadhaar(searchInput);
      case 'pan':
        return validatePAN(searchInput);
      default:
        return false;
    }
  };

  // Search API call (from Payments.js)
  const handleSearch = async () => {
    if (!isValidInput()) {
      setSearchError(getValidationMessage());
      return;
    }

    // Reset table data immediately when search button is clicked
    resetCollectionFields();
    setTotalCollection(0);
    setTotalCollectionDisplay('');
    
    setIsSearching(true);
    setSearchError('');

    try {
      const searchField = searchMode === 'loanid' ? 'hhnumber' : 
                         searchMode === 'mobile' ? 'registered_mobile_no' : 
                         searchMode === 'aadhaar' ? 'adhar_no' : 'pan_no';

      // Convert loan ID and PAN to uppercase for API, and trim last 3 characters for loan ID
      let searchValue = (searchMode === 'loanid' || searchMode === 'pan') 
        ? searchInput.toUpperCase() 
        : searchInput.toString();
      
      // Trim last 3 characters from loan ID only if it ends with N01, N02, etc. pattern
      // (e.g., MP011LE001640N01 -> MP011LE001640, but MP011LE001640 remains as is)
      if (searchMode === 'loanid' && /N\d{2}$/.test(searchValue)) {
        searchValue = searchValue.slice(0, -3);
      }

      const endpoint = ishhdata ? "_latest_outstanding_amount" : "_latest_household_payment";
      
      const response = await customFetch(
        `${process.env.REACT_APP_CLOUD}/${process.env.REACT_APP_ENVIRONMENT}${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            [searchField]: searchValue,
          }),
        }
      );

      const data = await response.json();

      // Check for no_bill_found response
      if (data.response_code === 'no_bill_found' && data.status === 'failed') {
        setSearchError('No bill found');
        setShowDataCard(false);
        setItems(null);
      } else if (data.error) {
        setSearchError(data.error);
        setShowDataCard(false);
        setItems(null);
      } else {
        // Set the fetched data - handle both old and new API response formats
        setItems({
          Lead_Name: data.name,
          HH_ID: data.hhnumber,
          Household_mobile_no: data.mobile_no,
          aadhaar_no: data.adhar_no || data.aadhaar_no,
          pan_no: data.pan_no,
          Balance: parseFloat(data.balance || data.amount || 0),
          tot_overdue: parseFloat(data.tot_overdue || 0),
          Charges_os: parseFloat(data.charges_os || data.Charges_os || 0),
          emi: parseFloat(data.emi || 0),
          Loan_os: parseFloat(data.loan_os || data.Loan_os || 0),
          Demand: parseFloat(data.demand || data.Demand || data.balance || data.amount || 0),
          Collected: parseFloat(data.collection || data.Collected || 0),
          disbursement_amt: data.disbursement_amt,
          csf: data.csf,
          row_id: data.row_id,
          household_id: data.hhnumber,
        });
        setSearchError('');
        setIsFromURLParam(false); // Mark as manual search
        setIsSearchInputDisabled(true); // Disable search input after successful search
        setIsCollectionConfirmed(true); // Start with fields disabled - enable only when "Change Amount" is clicked
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Failed to search. Please try again.');
      setShowDataCard(false);
      setItems(null);
    } finally {
      setIsSearching(false);
    }
  };
  

  // Load Razorpay SDK
  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Function to handle cancel on confirmation modal
  const handleConfirmationCancel = () => {
    setShowConfirmationModal(false);
    setConfirmationMode('');
  };

  // Handle collection mode change
  const handleModeChange = (e) => {
    const selectedMode = e.target.value;
    setCollectionMode(selectedMode);
    
    // Dismiss all existing toasts before showing new one
    toast.dismiss();
    
    // Show toast notification to acknowledge mode selection
    const modeDisplayName = selectedMode === 'Loan ID' ? 'Loan ID' : 
                           selectedMode === 'Mobile Number' ? 'Mobile Number' : 
                           selectedMode === 'Aadhaar' ? 'Aadhaar' : 
                           selectedMode === 'PAN Card' ? 'PAN Card' : 
                           selectedMode;
    toast.success(`Search mode switched to: ${modeDisplayName}`, {
      duration: 2000,
      icon: selectedMode === 'Loan ID' ? '🏠' :
            selectedMode === 'Mobile Number' ? '📱' : 
            selectedMode === 'Aadhaar' ? '🆔' : 
            selectedMode === 'PAN Card' ? '💳' : '🔍'
    });
  };

  // Function to handle confirmed submission after user clicks OK
  const handleConfirmedSubmit = async () => {
    // Close confirmation modal
    setShowConfirmationModal(false);
    setIsSubmitting(true);

    // Call Razorpay payment
    try {
      await handlePayNow();
    } catch (error) {
      console.error('Payment processing error:', error);
      if (error.message) {
        toast.error(`An error occurred: ${error.message}`);
      } else {
        toast.error('An error occurred while processing the payment');
      }
      setIsSubmitting(false);
    }
  };

  // Direct submit function - shows confirmation modal first
  const handleDirectSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!totalCollection || totalCollection <= 0) {
      toast.error('Please enter a valid collection amount');
      return;
    }

    if (!items || !items.HH_ID) {
      toast.error('Please search for a household first');
      return;
    }

    // Show confirmation modal before proceeding
    setConfirmationMode('Razorpay Payment');
    setShowConfirmationModal(true);
  };

  // Razorpay Payment (from Payments.js callRazorpaySDK)
  const handlePayNow = async () => {
    if (!items || !items.HH_ID) {
      toast.error('Please search for a household first');
      return;
    }

    if (!totalCollection || totalCollection <= 0) {
      toast.error('Please enter a valid collection amount');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get user ID from localStorage
      const uid = localStorage.getItem("in_userid");
      
      // Always round up decimal amounts before sending to Razorpay (both manual entry and auto-allocation)
      const roundedTotalCollection = Math.ceil(totalCollection);
      const roundedOdCollection = Math.ceil(odCollection || 0);
      const roundedCurrentCollection = Math.ceil(currentCollection || 0);
      const roundedChargeCollection = Math.ceil(chargeCollection || 0);
      const roundedAdvanceEmiCollection = Math.ceil(advanceEmiCollection || 0);
      const roundedPrepaymentCollection = Math.ceil(prepaymentCollection || 0);
      const roundedForeclosureCollection = Math.ceil(foreclosureCollection || 0);
      const roundedForeclosureCharges = Math.ceil(foreclosureCharges || 0);
      const roundedNetForeclosureAmount = Math.ceil(netForeclosureAmount || 0);
      
      // Create order
      const formdata = new FormData();
      formdata.append('in_household_id', items.household_id || items.HH_ID);
      formdata.append('household_id', items.HH_ID);
      formdata.append('in_amount_collected', roundedTotalCollection);
      formdata.append('amount', roundedTotalCollection);
      formdata.append('in_charges_collected', roundedChargeCollection);
      formdata.append('in_collection_mode_flag', 'R'); // R for Razorpay
      formdata.append('in_reference_number', '');
      formdata.append('in_remark', 'Razorpay payment via PaymentModal');
      formdata.append('in_userid', uid || '');
      formdata.append('in_collection_type', 'P');
      formdata.append('csf', items.csf || '');
      formdata.append('product_id', process.env.REACT_APP_PRODUCTID || '');
      formdata.append('payment_type', 'collection');
      
      // Add collection allocation values (all rounded)
      formdata.append('odCollection', roundedOdCollection);
      formdata.append('currentCollection', roundedCurrentCollection);
      formdata.append('chargeCollection', roundedChargeCollection);
      formdata.append('advanceEmiCollection', roundedAdvanceEmiCollection);
      formdata.append('prepaymentCollection', roundedPrepaymentCollection);
      formdata.append('prepaymentCount', maxPrepaymentEmis || 2);
      formdata.append('foreclosureCollection', roundedForeclosureCollection);
      formdata.append('foreclosureCharges', roundedForeclosureCharges);
      formdata.append('netForeclosureAmount', roundedNetForeclosureAmount);
      formdata.append('searchMode', searchMode); // 'loanid', 'mobile', 'aadhaar', 'pan'

      const response = await customFetch(process.env.REACT_APP_URL + '/order/create', {
        method: 'POST',
        body: formdata,
      });

      const orderData = await response.json();
      const { amount, id: order_id, currency } = orderData[0];

      // Load Razorpay SDK
      const res = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
      if (!res) {
        toast.error('Razorpay SDK failed to load');
        setIsSubmitting(false);
        return;
      }

      // Razorpay options
      const options = {
        key: process.env.REACT_APP_RZP_KEY,
        amount: amount,
        currency: currency,
        name: 'Navadhan Capital Private Limited',
        description: items.HH_ID,
        image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQKoCImzRGYjIXBOLGvldo_SKveUrqk1OiXhIg90e5XGQ&s',
        order_id: order_id,
        handler: (response) => {
          // Success response from Razorpay
          const paymentData = {
            paymentId: response.razorpay_payment_id,
            order_id: response.razorpay_order_id,
            signature: response.razorpay_signature,
          };

          console.log('Payment successful:', paymentData);
          
          setPaymentId(response.razorpay_payment_id);
          setShowSuccessModal(true);
          toast.success('Payment Successful!');
          
          // Update row flag
          updateRowFlag();
        },
        prefill: {
          name: items.Lead_Name || '',
          email: '',
          contact: items.Household_mobile_no || '',
        },
        notes: {
          hh_number: items.HH_ID,
          odCollection: odCollection || 0,
          currentCollection: currentCollection || 0,
          chargeCollection: chargeCollection || 0,
          advanceEmiCollection: advanceEmiCollection || 0,
          prepaymentCollection: prepaymentCollection || 0,
          foreclosureCollection: foreclosureCollection || 0,
          foreclosureCharges: foreclosureCharges || 0,
          netForeclosureAmount: netForeclosureAmount || 0,
          ...(items.csf ? { csf: items.csf } : {}),
          payment_type: 'collection',
          prepaymentCount: maxPrepaymentEmis || 2,
        },
        theme: {
          color: '#3399cc',
        },
        method: {
          card: false,
          netbanking: false,
          wallet: false,
          bank_transfer: false,
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.on('payment.failed', function(response) {
        console.error('Payment failed:', response.error);
        toast.error('Payment failed. Please try again.');
      });
      paymentObject.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update row flag after payment
  const updateRowFlag = async () => {
    if (!items || !items.row_id) return;

    try {
      await customFetch(
        `${process.env.REACT_APP_CLOUD}/${process.env.REACT_APP_ENVIRONMENT}_update_row_flag_payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            row_id: items.row_id,
          }),
        }
      );
    } catch (error) {
      console.error('Error updating row flag:', error);
    }
  };

  // Fetch updated remaining demand after payment
  const fetchUpdatedDemand = async () => {
    if (!items || !items.HH_ID) return;

    try {
      const response = await customFetch(
        `${process.env.REACT_APP_CLOUD}/${process.env.REACT_APP_ENVIRONMENT}_latest_household_payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            hhnumber: items.HH_ID.toString(),
          }),
        }
      );

      const data = await response.json();

      if (data.error) {
        console.error('Error fetching updated demand:', data.error);
        return;
      }

      // Update items with latest data
      setItems({
        Lead_Name: data.name,
        HH_ID: data.hhnumber,
        Household_mobile_no: data.mobile_no,
        aadhaar_no: data.adhar_no || data.aadhaar_no,
        pan_no: data.pan_no,
        Balance: parseFloat(data.balance || data.amount || 0),
        tot_overdue: parseFloat(data.tot_overdue || 0),
        Charges_os: parseFloat(data.charges_os || data.Charges_os || 0),
        emi: parseFloat(data.emi || 0),
        Loan_os: parseFloat(data.loan_os || data.Loan_os || 0),
        Demand: parseFloat(data.demand || data.Demand || data.balance || data.amount || 0),
        Collected: parseFloat(data.collection || data.Collected || 0),
        disbursement_amt: data.disbursement_amt,
        csf: data.csf,
        row_id: data.row_id,
        household_id: data.hhnumber,
      });

      // Reset payment fields
      setTotalCollection(0);
      setTotalCollectionDisplay('');
      resetCollectionFields();

      toast.success('Demand updated successfully');
    } catch (error) {
      console.error('Error fetching updated demand:', error);
    }
  };

  // Clear/Reset function
  const handleClear = () => {
    setSearchInput('');
    setSearchError('');
    setItems(null);
    setShowDataCard(false);
    setTotalCollection(0);
    setTotalCollectionDisplay('');
    setOdCollection(0);
    setCurrentCollection(0);
    setChargeCollection(0);
    setChargeCollectionDisplay('');
    setAdvanceEmiCollection(0);
    setAdvanceEmiDisplay('');
    setPrepaymentCollection(0);
    setPrepaymentDisplay('');
    setForeclosureCollection(0);
    setForeclosureDisplay('');
    setForeclosureCharges(0);
    setNetForeclosureAmount(0);
    setManuallyEditedFields(new Set());
    setAutoAllocatedFields(new Set());
    setHasShownOverflowMessage(false);
    setIsFromURLParam(false);
    setIsSearchInputDisabled(false);
    setIsCollectionConfirmed(false);
    toast.success('Form cleared successfully');
  };

  // Format currency helper
  const formatCurrency = (value, forceInteger = false) => {
    if (!value && value !== 0) return '';
    const strValue = String(value);
    const isNegative = strValue.includes('-');
    
    // Handle decimal values properly
    const numValue = parseFloat(strValue.replace(/[^0-9.-]/g, ''));
    if (isNaN(numValue)) return '';
    
    // In Cash Collection mode or when forceInteger is true, round to integer
    const shouldRoundToInteger = collectionMode === 'Cash Collection' || forceInteger;
    const valueToFormat = shouldRoundToInteger ? Math.round(numValue) : numValue;
    
    // Format with Indian locale
    const formatted = valueToFormat.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: shouldRoundToInteger ? 0 : 2
    });
    
    return isNegative ? `-${formatted.replace('-', '')}` : formatted;
  };

  // Handle total collection change
  const handleTotalCollectionChange = (e) => {
    let inputValue = e.target.value;
    const cleanValue = inputValue.replace(/,/g, '');
    const integerPart = cleanValue.split('.')[0];
    inputValue = integerPart.replace(/[^0-9]/g, '');
    const value = parseInt(inputValue) || 0;

    // If total is cleared (set to 0), reset all collection fields
    if (value === 0 || inputValue === '') {
      setTotalCollection(0);
      setTotalCollectionDisplay('');
      setOdCollection(0);
      setCurrentCollection(0);
      setChargeCollection(0);
      setChargeCollectionDisplay('');
      setAdvanceEmiCollection(0);
      setAdvanceEmiDisplay('');
      setPrepaymentCollection(0);
      setPrepaymentDisplay('');
      setForeclosureCollection(0);
      setForeclosureDisplay('');
      setForeclosureCharges(0);
      setNetForeclosureAmount(0);
      setShowForeclosureCharges(false);
      setManuallyEditedFields(new Set());
      setAutoAllocatedFields(new Set());
      setHasUserEnteredTotal(false);
      return;
    }

    // Store original total collection whenever user enters/changes a value
    if (value > 0) {
      setHasUserEnteredTotal(true);
    }

    // Clear manual field tracking when total changes to allow fresh auto-allocation
    if (value > 0 && value !== totalCollection) {
      setManuallyEditedFields(new Set());
      setAutoAllocatedFields(new Set());
    }

    setTotalCollection(value);
    setTotalCollectionDisplay(inputValue);
    
    // Auto-allocate immediately when amount is entered (after clicking Change Amount)
    // Pass isManualEntry=true since user is typing
    if (value > 0 && !isCollectionConfirmed) {
      autoAllocateCollection(value, true);
    }
  };

  // Helper functions for pre-demand payments
  const isPreDemandPayment = () => {
    return remainingDemand.current <= 0;
  };

  const calculatePreDemandAllocation = (totalAmount) => {
    let remaining = totalAmount;
    let od = 0, current = 0, charge = 0, nextEmi = 0, prepayment = 0;

    // Priority 1: OD allocation
    const odDemand = remainingDemand.od;
    if (remaining > 0 && odDemand > 0) {
      od = Math.min(remaining, odDemand);
      remaining = parseFloat((remaining - od).toFixed(2));
    }

    // Priority 2: Skip Current Demand (CD = 0 in pre-demand scenario)
    current = 0;

    // Handle manually controlled fields
    if (manuallyEditedFields.has('charges')) {
      remaining = parseFloat((remaining - chargeCollection).toFixed(2));
      charge = chargeCollection;
    }
    if (manuallyEditedFields.has('nextEmi')) {
      // Cap manually entered Next EMI to the EMI demand to enforce business rules
      const cappedNextEmi = Math.min(advanceEmiCollection, emiAmount);
      remaining = parseFloat((remaining - cappedNextEmi).toFixed(2));
      nextEmi = cappedNextEmi;
    }
    if (manuallyEditedFields.has('prepayment')) {
      remaining = parseFloat((remaining - prepaymentCollection).toFixed(2));
      prepayment = prepaymentCollection;
    }

    // Priority 3: Charges
    const chargeDemand = remainingDemand.charges;
    if (remaining > 0 && chargeDemand > 0 && !manuallyEditedFields.has('charges')) {
      charge = Math.min(remaining, chargeDemand);
      remaining = parseFloat((remaining - charge).toFixed(2));
    }

    // Priority 4: Next EMI
    const remainingNextEmiCapacity = remainingDemand.nextEmi;
    if (remaining > 0 && remainingNextEmiCapacity > 0 && !manuallyEditedFields.has('nextEmi')) {
      nextEmi = Math.min(remaining, remainingNextEmiCapacity);
      remaining = parseFloat((remaining - nextEmi).toFixed(2));
    }

    // Priority 5: Prepayment/Foreclosure
    let foreclosure = 0;
    if (remaining > 0 && !manuallyEditedFields.has('prepayment')) {
      if (remaining <= maxPrepaymentAmount && maxPrepaymentAmount > 0) {
        prepayment = remaining;
        remaining = 0;
      } else if (maxPrepaymentAmount > 0) {
        prepayment = maxPrepaymentAmount;
        const overflowAmount = remaining - maxPrepaymentAmount;
        remaining = 0;
        
        // When amount overflows from prepayment, split using 96/100 + 4/100
        // Use Math.ceil() for foreclosure (96%) and calculate charges (4%) as remainder to maintain exact total
        const netForeclosureToAdd = Math.ceil((overflowAmount * 100) / 104);
        const foreclosureChargesToAdd = overflowAmount - netForeclosureToAdd;
        
        setForeclosureCharges(foreclosureChargesToAdd);
        setNetForeclosureAmount(netForeclosureToAdd);
        setShowForeclosureCharges(true);
        setForeclosureCollection(netForeclosureToAdd);
        setForeclosureDisplay(netForeclosureToAdd > 0 ? netForeclosureToAdd.toString() : '');
        foreclosure = netForeclosureToAdd;
      } else {
        // No prepayment capacity (all demands are 0), entire amount goes to foreclosure
        // Direct foreclosure payment: split using proper formula
        // Use Math.ceil() for foreclosure (96%) and calculate charges (4%) as remainder to maintain exact total
        const netForeclosureToAdd = Math.ceil((remaining * 100) / 104);
        const foreclosureChargesToAdd = remaining - netForeclosureToAdd;
        remaining = 0;
        
        setForeclosureCharges(foreclosureChargesToAdd);
        setNetForeclosureAmount(netForeclosureToAdd);
        setShowForeclosureCharges(true);
        setForeclosureCollection(netForeclosureToAdd);
        setForeclosureDisplay(netForeclosureToAdd > 0 ? netForeclosureToAdd.toFixed(2).replace(/\.00$/, '') : '');
        foreclosure = netForeclosureToAdd;
      }
    }

    // For Cash Collection mode, round all amounts to integers to avoid decimal issues
    // EXCEPT foreclosure which needs to maintain 96/4 split precision
    if (collectionMode === 'Cash Collection') {
      od = Math.round(od);
      current = Math.round(current);
      charge = Math.round(charge);
      nextEmi = Math.round(nextEmi);
      prepayment = Math.round(prepayment);
      // foreclosure = Math.round(foreclosure); // DON'T round - needs to maintain 96/4 split
      remaining = Math.round(remaining);
    }

    return { od, current, charge, nextEmi, prepayment, remaining, foreclosure };
  };

  // Main allocation calculation function
  const calculateAllocation = (totalAmount) => {
    if (isPreDemandPayment()) {
      return calculatePreDemandAllocation(totalAmount);
    }

    let remaining = totalAmount;
    let od = 0, current = 0, charge = 0, nextEmi = 0, prepayment = 0;

    const remainingOdDemand = remainingDemand.od;
    const remainingCurrentDemand = remainingDemand.current;
    const remainingChargeDemand = remainingDemand.charges;
    const totalAllowedCharges = remainingChargeDemand + nachBounceCharge;
    const remainingNextEmiCapacity = remainingDemand.nextEmi;
    
    const hasOdDemand = remainingOdDemand > 0;

    // Handle manually controlled fields
    if (manuallyEditedFields.has('charges')) {
      remaining = parseFloat((remaining - chargeCollection).toFixed(2));
      charge = chargeCollection;
    }
    if (manuallyEditedFields.has('nextEmi')) {
      // Cap manually entered Next EMI to the remaining Next EMI capacity to enforce business rules
      const cappedNextEmi = Math.min(advanceEmiCollection, remainingNextEmiCapacity);
      remaining = parseFloat((remaining - cappedNextEmi).toFixed(2));
      nextEmi = cappedNextEmi;
    }
    if (manuallyEditedFields.has('prepayment')) {
      remaining = parseFloat((remaining - prepaymentCollection).toFixed(2));
      prepayment = prepaymentCollection;
    }

    // Priority 1: OD allocation
    if (remaining > 0 && hasOdDemand) {
      od = Math.min(remaining, remainingOdDemand);
      remaining = parseFloat((remaining - od).toFixed(2));
    }

    // Priority 2: Current demand allocation
    if (remaining > 0 && remainingCurrentDemand > 0) {
      current = Math.min(remaining, remainingCurrentDemand);
      remaining = parseFloat((remaining - current).toFixed(2));
    }

    // Priority 3: Charge allocation
    if (remaining > 0 && remainingChargeDemand > 0 && !manuallyEditedFields.has('charges')) {
      charge = Math.min(remaining, remainingChargeDemand);
      remaining = parseFloat((remaining - charge).toFixed(2));
    }

    // Priority 4: Next EMI allocation
    if (remaining > 0 && remainingNextEmiCapacity > 0 && !manuallyEditedFields.has('nextEmi')) {
      nextEmi = Math.min(remaining, remainingNextEmiCapacity);
      remaining = parseFloat((remaining - nextEmi).toFixed(2));
    }

    // Priority 5: Prepayment/Foreclosure
    let foreclosure = 0;
    if (remaining > 0 && !manuallyEditedFields.has('prepayment')) {
      if (remaining <= maxPrepaymentAmount && maxPrepaymentAmount > 0) {
        prepayment = remaining;
        remaining = 0;
      } else if (maxPrepaymentAmount > 0) {
        prepayment = maxPrepaymentAmount;
        const overflowAmount = remaining - maxPrepaymentAmount;
        remaining = 0;
        
        // When amount overflows from prepayment, split using 96/100 + 4/100
        // Use Math.ceil() for foreclosure (96%) and calculate charges (4%) as remainder to maintain exact total
        const netForeclosureToAdd = Math.ceil((overflowAmount * 100) / 104);
        const foreclosureChargesToAdd = overflowAmount - netForeclosureToAdd;
        
        setForeclosureCharges(foreclosureChargesToAdd);
        setNetForeclosureAmount(netForeclosureToAdd);
        setShowForeclosureCharges(true);
        setForeclosureCollection(netForeclosureToAdd);
        setForeclosureDisplay(netForeclosureToAdd > 0 ? netForeclosureToAdd.toString() : '');
        foreclosure = netForeclosureToAdd;
      } else {
        // No prepayment capacity (all demands are 0), entire amount goes to foreclosure
        // Direct foreclosure payment: split using proper formula
        // Use Math.ceil() for foreclosure (96%) and calculate charges (4%) as remainder to maintain exact total
        const netForeclosureToAdd = Math.ceil((remaining * 100) / 104);
        const foreclosureChargesToAdd = remaining - netForeclosureToAdd;
        remaining = 0;
        
        setForeclosureCharges(foreclosureChargesToAdd);
        setNetForeclosureAmount(netForeclosureToAdd);
        setShowForeclosureCharges(true);
        setForeclosureCollection(netForeclosureToAdd);
        setForeclosureDisplay(netForeclosureToAdd > 0 ? netForeclosureToAdd.toFixed(2).replace(/\.00$/, '') : '');
        foreclosure = netForeclosureToAdd;
      }
    }

    // For Cash Collection mode, round all amounts to integers to avoid decimal issues
    // EXCEPT foreclosure which needs to maintain 96/4 split precision
    if (collectionMode === 'Cash Collection') {
      od = Math.round(od);
      current = Math.round(current);
      charge = Math.round(charge);
      nextEmi = Math.round(nextEmi);
      prepayment = Math.round(prepayment);
      // foreclosure = Math.round(foreclosure); // DON'T round - needs to maintain 96/4 split
      remaining = Math.round(remaining);
    }

    return { od, current, charge, nextEmi, prepayment, remaining, foreclosure };
  };

  // Wrapper function to apply allocation and update states
  const autoAllocateCollection = (total, isManualEntry = false) => {
    let roundedTotal, roundedOd, roundedCurrent, roundedCharge, roundedNextEmi, roundedPrepayment, roundedForeclosure;
    
    if (isManualEntry) {
      // MANUAL ENTRY: Round UP the total (24,803.26 → 24,804), then adjust allocations to match
      roundedTotal = Math.ceil(total);
      
      // Calculate allocation with the rounded total
      const allocation = calculateAllocation(roundedTotal);
      
      // Use floor for allocations to respect demand limits
      roundedOd = Math.floor(allocation.od || 0);
      roundedCurrent = Math.floor(allocation.current || 0);
      roundedCharge = Math.floor(allocation.charge || 0);
      roundedNextEmi = Math.floor(allocation.nextEmi || 0);
      roundedPrepayment = Math.floor(allocation.prepayment || 0);
      roundedForeclosure = Math.floor(allocation.foreclosure || 0);
      
      // Calculate sum and difference
      let sum = roundedOd + roundedCurrent + roundedCharge + roundedNextEmi + roundedPrepayment + roundedForeclosure;
      let diff = roundedTotal - sum;
      
      // Distribute difference following priority: OD → Current → Charges → Next EMI → Advance → Foreclosure
      // BUT respect demand limits - if a field exceeds its demand, move excess to next priority
      if (diff > 0) {
        // Try OD first (respect demand limit)
        if (roundedOd > 0 && roundedOd < remainingDemand.od) {
          const availableOdRoom = remainingDemand.od - roundedOd;
          const addToOd = Math.min(diff, availableOdRoom);
          roundedOd += addToOd;
          diff -= addToOd;
        }
        
        // Try Current (respect demand limit)
        if (diff > 0 && roundedCurrent > 0 && roundedCurrent < remainingDemand.current) {
          const availableCurrentRoom = remainingDemand.current - roundedCurrent;
          const addToCurrent = Math.min(diff, availableCurrentRoom);
          roundedCurrent += addToCurrent;
          diff -= addToCurrent;
        }
        
        // Try Charges (respect demand limit)
        if (diff > 0 && roundedCharge > 0 && roundedCharge < remainingDemand.charges) {
          const availableChargeRoom = remainingDemand.charges - roundedCharge;
          const addToCharge = Math.min(diff, availableChargeRoom);
          roundedCharge += addToCharge;
          diff -= addToCharge;
        }
        
        // Excess beyond Charges demand goes to Next EMI → Advance → Foreclosure
        if (diff > 0) {
          // Try Next EMI (respect demand limit)
          const availableNextEmiRoom = remainingDemand.nextEmi - roundedNextEmi;
          if (availableNextEmiRoom > 0) {
            const addToNextEmi = Math.min(diff, availableNextEmiRoom);
            roundedNextEmi += addToNextEmi;
            diff -= addToNextEmi;
          }
        }
        
        // Remaining goes to Advance (prepayment)
        if (diff > 0) {
          const availablePrepaymentRoom = maxPrepaymentAmount - roundedPrepayment;
          if (availablePrepaymentRoom > 0) {
            const addToPrepayment = Math.min(diff, availablePrepaymentRoom);
            roundedPrepayment += addToPrepayment;
            diff -= addToPrepayment;
          }
        }
        
        // Any remaining goes to Foreclosure
        if (diff > 0) {
          roundedForeclosure += diff;
          
          // Recalculate foreclosure charges to maintain 96/4 split
          // Total foreclosure amount = roundedForeclosure
          // This should be split: net (96%) + charges (4%)
          // Using formula: netForeclosure = Math.ceil((total * 100) / 104)
          const totalForeclosureAmount = roundedForeclosure;
          const recalculatedNetForeclosure = Math.ceil((totalForeclosureAmount * 100) / 104);
          const recalculatedCharges = totalForeclosureAmount - recalculatedNetForeclosure;
          
          // Update foreclosure states with recalculated values
          setForeclosureCharges(recalculatedCharges);
          setNetForeclosureAmount(recalculatedNetForeclosure);
          setShowForeclosureCharges(recalculatedCharges > 0);
          
          // Important: roundedForeclosure should be the NET amount, not total
          roundedForeclosure = recalculatedNetForeclosure;
        }
      }
      
      // Update total collection with rounded value
      setTotalCollection(roundedTotal);
      setTotalCollectionDisplay(formatCurrency(roundedTotal));
    } else {
      // CONFIRM BUTTON (Auto-allocation): Keep everything as exact decimal values, NO rounding
      roundedTotal = total; // Keep exact decimal value (24,803.26)
      
      // Calculate allocation with the exact total
      const allocation = calculateAllocation(roundedTotal);
      
      // Keep exact decimal values - NO rounding
      roundedOd = allocation.od || 0; // 10,751 stays as-is
      roundedCurrent = allocation.current || 0; // 10,751.92 stays as-is
      roundedCharge = allocation.charge || 0; // 3,300.34 stays as-is
      roundedNextEmi = allocation.nextEmi || 0;
      roundedPrepayment = allocation.prepayment || 0;
      roundedForeclosure = allocation.foreclosure || 0;
    }
    
    // Apply allocation to states
    setOdCollection(roundedOd);
    setCurrentCollection(roundedCurrent);
    
    if (!manuallyEditedFields.has('charges')) {
      setChargeCollection(Math.max(0, roundedCharge));
      setChargeCollectionDisplay(roundedCharge > 0 ? roundedCharge.toString() : '');
    }
    
    if (!manuallyEditedFields.has('nextEmi')) {
      setAdvanceEmiCollection(roundedNextEmi);
      setAdvanceEmiDisplay(roundedNextEmi > 0 ? roundedNextEmi.toString() : '');
    }
    
    if (!manuallyEditedFields.has('prepayment')) {
      setPrepaymentCollection(roundedPrepayment);
      setPrepaymentDisplay(roundedPrepayment > 0 ? roundedPrepayment.toString() : '');
    }
    
    if (roundedForeclosure > 0) {
      setForeclosureCollection(roundedForeclosure);
      setForeclosureDisplay(roundedForeclosure.toString());
    } else {
      setForeclosureCollection(0);
      setForeclosureDisplay('');
      setForeclosureCharges(0);
      setNetForeclosureAmount(0);
      setShowForeclosureCharges(false);
    }
  };

  // Legacy simplified allocation (keeping for reference)
  const autoAllocateCollectionSimple = (total) => {
    let remaining = total;

    // OD
    const odAmount = Math.min(remaining, remainingDemand.od);
    setOdCollection(odAmount);
    remaining -= odAmount;

    // Current
    const currentAmount = Math.min(remaining, remainingDemand.current);
    setCurrentCollection(currentAmount);
    remaining -= currentAmount;

    // Charges
    const chargesAmount = Math.min(remaining, remainingDemand.charges);
    setChargeCollection(chargesAmount);
    setChargeCollectionDisplay(chargesAmount.toString());
    remaining -= chargesAmount;

    // Next EMI
    const nextEmiAmount = Math.min(remaining, remainingDemand.nextEmi);
    setAdvanceEmiCollection(nextEmiAmount);
    setAdvanceEmiDisplay(nextEmiAmount.toString());
    remaining -= nextEmiAmount;

    // Prepayment
    const prepaymentAmount = Math.min(remaining, maxPrepaymentAmount);
    setPrepaymentCollection(prepaymentAmount);
    setPrepaymentDisplay(prepaymentAmount.toString());
    remaining -= prepaymentAmount;

    // Foreclosure (remaining)
    if (remaining > 0) {
      const netForeclosure = Math.ceil((remaining * 100) / 104);
      const foreclosureChargesAmount = remaining - netForeclosure;
      setForeclosureCollection(remaining);
      setForeclosureDisplay(remaining.toString());
      setNetForeclosureAmount(netForeclosure);
      setForeclosureCharges(foreclosureChargesAmount);
      setShowForeclosureCharges(true);
    } else {
      setForeclosureCollection(0);
      setForeclosureDisplay('');
      setNetForeclosureAmount(0);
      setForeclosureCharges(0);
      setShowForeclosureCharges(false);
    }
  };

  const resetCollectionFields = () => {
    setOdCollection(0);
    setCurrentCollection(0);
    setChargeCollection(0);
    setChargeCollectionDisplay('');
    setAdvanceEmiCollection(0);
    setAdvanceEmiDisplay('');
    setPrepaymentCollection(0);
    setPrepaymentDisplay('');
    setForeclosureCollection(0);
    setForeclosureDisplay('');
    setForeclosureCharges(0);
    setNetForeclosureAmount(0);
    setShowForeclosureCharges(false);
    
    // Clear all tracking states
    setManuallyEditedFields(new Set());
    setAutoAllocatedFields(new Set());
    setHasUserEnteredTotal(false);
    setIsCollectionConfirmed(false);
    setHasShownOverflowMessage(false);
    setShowPrepaymentTooltip(false);
    setShowForeclosureTooltip(false);
    
    // Reset refs
    isChargeTransferRef.current = false;
    isNextEmiFromTransferRef.current = false;
    isManualUpdateRef.current = false;
    previousChargeAmountRef.current = 0;
    previousNextEmiAmountRef.current = 0;
  };

  const handleTotalCollectionFocus = (e) => {
    if (!totalCollection || totalCollection === 0) {
      e.target.select();
    }
    if (totalCollection === 0) {
      setTotalCollectionDisplay('');
    } else {
      // For manual entry, autoAllocateCollection will round up the total
      if (!isCollectionConfirmed) {
        autoAllocateCollection(totalCollection, true);
      } else {
        // If already confirmed, just show current value
        setTotalCollectionDisplay(totalCollection.toString());
      }
    }
  };

  const handleTotalCollectionBlur = (e) => {
    if (totalCollection > 0) {
      // Round UP to nearest integer (26,105.37 → 26,106)
      const roundedValue = Math.ceil(totalCollection);
      setTotalCollectionDisplay(formatCurrency(roundedValue));
    } else {
      setTotalCollectionDisplay('');
    }
  };

  // Debounced toast error function to prevent duplicate messages
  const lastToastMessageRef = useRef('');
  const lastToastTimeRef = useRef(0);
  const TOAST_DEBOUNCE_MS = 2000;

  const showDebouncedToastError = (message) => {
    const now = Date.now();
    const timeSinceLastToast = now - lastToastTimeRef.current;
    const isSameMessage = lastToastMessageRef.current === message;
    
    if (!isSameMessage || timeSinceLastToast > TOAST_DEBOUNCE_MS) {
      toast.error(message);
      lastToastMessageRef.current = message;
      lastToastTimeRef.current = now;
    }
  };

  // Handle charge collection change - Full functionality from CollectionModal.jsx
  const handleChargeCollectionChange = (e) => {
    let inputValue = e.target.value;
    const cleanValue = inputValue.replace(/,/g, '');
    const integerPart = cleanValue.split('.')[0];
    inputValue = integerPart.replace(/[^0-9]/g, '');
    const value = parseInt(inputValue) || 0;
    
    // Get previous charge amount for delta calculation
    const previousChargeAmount = chargeCollection || 0;
    
    // Get max charge limit from remaining demand + standard EMI from API (not calculated emiAmount)
    const maxChargeLimit = remainingDemand.charges + standardEmi;
    
    // VALIDATION 1: Check if the new charge value would exceed the total collection
    const coreAllocation = odCollection + currentCollection;
    const maxAllowableChargeFromTotal = totalCollection - coreAllocation;
    
    if (value > maxAllowableChargeFromTotal) {
      showDebouncedToastError("Amount doesn't match total collected. Please update total collected first");
      return;
    }
    
    // VALIDATION 2: Don't allow charges to exceed the total allowed charges
    if (value > maxChargeLimit) {
      showDebouncedToastError(`Charges cannot exceed ₹${formatCurrency(maxChargeLimit)} (Charge Due: ₹${formatCurrency(remainingDemand.charges)} + EMI: ₹${formatCurrency(standardEmi)})`);
      return;
    }
    
    // VALIDATION 3: If user is INCREASING charges, check if there's enough in Foreclosure + Prepayment + Next EMI to take back
    if (value > previousChargeAmount) {
      const deltaToTakeBack = value - previousChargeAmount;
      const currentForeclosureAmount = foreclosureCollection || 0;
      const currentPrepaymentAmount = prepaymentCollection || 0;
      const currentNextEmiAmount = advanceEmiCollection || 0;
      const totalAvailableToTakeBack = currentForeclosureAmount + currentPrepaymentAmount + currentNextEmiAmount;
      
      // If trying to increase more than what's available in Foreclosure + Prepayment + Next EMI
      if (deltaToTakeBack > totalAvailableToTakeBack) {
        // Check if both Next EMI and Prepayment have insufficient funds
        const availableInAdvanceFields = currentPrepaymentAmount + currentNextEmiAmount;
        if (availableInAdvanceFields === 0 || deltaToTakeBack > availableInAdvanceFields) {
          showDebouncedToastError("Insufficient amount. Please increase the collected amount to apply charge.");
          return;
        }
        
        const maxAllowableCharge = previousChargeAmount + totalAvailableToTakeBack;
        showDebouncedToastError(`You are trying to add ₹${formatCurrency(value)} in charges. Only ₹${formatCurrency(totalAvailableToTakeBack)} available in Foreclosure/Prepayment/Next EMI to allocate. Maximum charge you can enter is ₹${formatCurrency(maxAllowableCharge)}`);
        return;
      }
    }
    
    // Set flags to prevent automatic allocation during typing
    isChargeTransferRef.current = true;
    setIsUpdatingFromManualFields(true);
    isManualUpdateRef.current = true;
    
    // Mark this field as manually edited
    setManuallyEditedFields(prev => new Set(prev).add('charges'));
    
    // Always allow typing, keep raw input
    setChargeCollection(value);
    setChargeCollectionDisplay(inputValue);
    
    // If user clears the field, transfer to Next EMI → Prepayment → Foreclosure
    if (inputValue === '' || inputValue === '0') {
      setChargeCollection(0);
      
      if (previousChargeAmount > 0) {
        const deltaToTransfer = previousChargeAmount;
        
        // Check remaining Next EMI capacity
        const remainingNextEmiCapacity = remainingDemand.nextEmi;
        const currentNextEmiAmount = advanceEmiCollection || 0;
        const nextEmiRoom = Math.max(0, remainingNextEmiCapacity - currentNextEmiAmount);
        
        // Transfer to Next EMI first, then check prepayment capacity, overflow to Foreclosure
        if (nextEmiRoom > 0) {
          const amountToNextEmi = Math.min(deltaToTransfer, nextEmiRoom);
          const remainingAfterNextEmi = deltaToTransfer - amountToNextEmi;
          
          const newNextEmiAmount = currentNextEmiAmount + amountToNextEmi;
          setAdvanceEmiCollection(newNextEmiAmount);
          setAdvanceEmiDisplay(newNextEmiAmount > 0 ? newNextEmiAmount.toString() : '');
          setManuallyEditedFields(prev => new Set(prev).add('nextEmi'));
          setAutoAllocatedFields(prev => new Set(prev).add('nextEmi'));
          
          if (amountToNextEmi > 0) {
            isNextEmiFromTransferRef.current = true;
          }
          
          // Check prepayment capacity for remaining amount
          if (remainingAfterNextEmi > 0) {
            const currentPrepaymentAmount = prepaymentCollection || 0;
            const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
            
            if (prepaymentRoom > 0) {
              const amountToPrepayment = Math.min(remainingAfterNextEmi, prepaymentRoom);
              const amountToForeclosure = remainingAfterNextEmi - amountToPrepayment;
              
              // Allocate to prepayment
              if (amountToPrepayment > 0) {
                const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
                setPrepaymentCollection(newPrepaymentAmount);
                setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
                setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
                setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
              }
              
              // Allocate overflow to foreclosure
              if (amountToForeclosure > 0) {
                // When transferring from charges, split: 96% to foreclosure, 4% to charges
                const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
                const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
                
                const currentForeclosureAmount = foreclosureCollection || 0;
                const currentForeclosureCharges = foreclosureCharges || 0;
                
                const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
                const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
                
                setForeclosureCollection(newNetForeclosure);
                setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
                setForeclosureCharges(newForeclosureCharges);
                setNetForeclosureAmount(newNetForeclosure);
                setShowForeclosureCharges(true);
              }
            } else {
              // Prepayment is full, all goes to foreclosure
              // When transferring from charges, split: 96% to foreclosure, 4% to charges
              const netForeclosureToAdd = Math.ceil((remainingAfterNextEmi * 100) / 104);
              const foreclosureChargesToAdd = remainingAfterNextEmi - netForeclosureToAdd;
              
              const currentForeclosureAmount = foreclosureCollection || 0;
              const currentForeclosureCharges = foreclosureCharges || 0;
              
              const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
              const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
              
              setForeclosureCollection(newNetForeclosure);
              setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
              setForeclosureCharges(newForeclosureCharges);
              setNetForeclosureAmount(newNetForeclosure);
              setShowForeclosureCharges(true);
            }
          }
        } else {
          // No Next EMI room, check prepayment capacity
          const currentPrepaymentAmount = prepaymentCollection || 0;
          const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
          
          if (prepaymentRoom > 0) {
            const amountToPrepayment = Math.min(deltaToTransfer, prepaymentRoom);
            const amountToForeclosure = deltaToTransfer - amountToPrepayment;
            
            // Allocate to prepayment
            if (amountToPrepayment > 0) {
              const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
              setPrepaymentCollection(newPrepaymentAmount);
              setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
              setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
              setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
            }
            
            // Allocate overflow to foreclosure
            if (amountToForeclosure > 0) {
              // When transferring from charges, split: 96% to foreclosure, 4% to charges
              const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
              const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
              
              const currentForeclosureAmount = foreclosureCollection || 0;
              const currentForeclosureCharges = foreclosureCharges || 0;
              
              const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
              const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
              
              setForeclosureCollection(newNetForeclosure);
              setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
              setForeclosureCharges(newForeclosureCharges);
              setNetForeclosureAmount(newNetForeclosure);
              setShowForeclosureCharges(true);
            }
          } else {
            // Both Next EMI and Prepayment are full, all goes to foreclosure
            // When transferring from charges, split: 96% to foreclosure, 4% to charges
            const netForeclosureToAdd = Math.ceil((deltaToTransfer * 100) / 104);
            const foreclosureChargesToAdd = deltaToTransfer - netForeclosureToAdd;
            
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
          }
        }
      }
    } else if (value < previousChargeAmount) {
      // User is REDUCING charges - transfer the delta to Next EMI → Prepayment → Foreclosure
      const deltaToTransfer = previousChargeAmount - value;
      
      const remainingNextEmiCapacity = remainingDemand.nextEmi;
      const currentNextEmiAmount = advanceEmiCollection || 0;
      const nextEmiRoom = Math.max(0, remainingNextEmiCapacity - currentNextEmiAmount);
      
      // Transfer to Next EMI first, then check prepayment capacity, overflow to Foreclosure
      if (nextEmiRoom > 0) {
        const amountToNextEmi = Math.min(deltaToTransfer, nextEmiRoom);
        const remainingAfterNextEmi = deltaToTransfer - amountToNextEmi;
        
        const newNextEmiAmount = currentNextEmiAmount + amountToNextEmi;
        setAdvanceEmiCollection(newNextEmiAmount);
        setAdvanceEmiDisplay(newNextEmiAmount > 0 ? newNextEmiAmount.toString() : '');
        setManuallyEditedFields(prev => new Set(prev).add('nextEmi'));
        setAutoAllocatedFields(prev => new Set(prev).add('nextEmi'));
        
        if (amountToNextEmi > 0) {
          isNextEmiFromTransferRef.current = true;
        }
        
        if (remainingAfterNextEmi > 0) {
          const currentPrepaymentAmount = prepaymentCollection || 0;
          const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
          
          if (prepaymentRoom > 0) {
            const amountToPrepayment = Math.min(remainingAfterNextEmi, prepaymentRoom);
            const amountToForeclosure = remainingAfterNextEmi - amountToPrepayment;
            
            // Allocate to prepayment
            if (amountToPrepayment > 0) {
              const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
              setPrepaymentCollection(newPrepaymentAmount);
              setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
              setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
              setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
            }
            
            // Allocate overflow to foreclosure
            if (amountToForeclosure > 0) {
              // When transferring from charges, split: 96% to foreclosure, 4% to charges
              const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
              const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
              
              const currentForeclosureAmount = foreclosureCollection || 0;
              const currentForeclosureCharges = foreclosureCharges || 0;
              
              const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
              const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
              
              setForeclosureCollection(newNetForeclosure);
              setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
              setForeclosureCharges(newForeclosureCharges);
              setNetForeclosureAmount(newNetForeclosure);
              setShowForeclosureCharges(true);
            }
          } else {
            // Prepayment is full, all goes to foreclosure
            // When transferring from charges, split: 96% to foreclosure, 4% to charges
            const netForeclosureToAdd = Math.ceil((remainingAfterNextEmi * 100) / 104);
            const foreclosureChargesToAdd = remainingAfterNextEmi - netForeclosureToAdd;
            
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
          }
        }
      } else {
        // No Next EMI room, check prepayment capacity
        const currentPrepaymentAmount = prepaymentCollection || 0;
        const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
        
        if (prepaymentRoom > 0) {
          const amountToPrepayment = Math.min(deltaToTransfer, prepaymentRoom);
          const amountToForeclosure = deltaToTransfer - amountToPrepayment;
          
          // Allocate to prepayment
          if (amountToPrepayment > 0) {
            const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
            setPrepaymentCollection(newPrepaymentAmount);
            setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
            setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
            setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
          }
          
          // Allocate overflow to foreclosure
          if (amountToForeclosure > 0) {
            // When transferring from charges, split: 96% to foreclosure, 4% to charges
            const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
            const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
            
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
          }
        } else {
          // Both Next EMI and Prepayment are full, all goes to foreclosure
          // When transferring from charges, split: 96% to foreclosure, 4% to charges
          const netForeclosureToAdd = Math.ceil((deltaToTransfer * 100) / 104);
          const foreclosureChargesToAdd = deltaToTransfer - netForeclosureToAdd;
          
          const currentForeclosureAmount = foreclosureCollection || 0;
          const currentForeclosureCharges = foreclosureCharges || 0;
          
          const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
          const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
          
          setForeclosureCollection(newNetForeclosure);
          setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
          setForeclosureCharges(newForeclosureCharges);
          setNetForeclosureAmount(newNetForeclosure);
          setShowForeclosureCharges(true);
        }
      }
    } else if (value > previousChargeAmount) {
      // User is INCREASING charges - take back from Foreclosure FIRST, then Prepayment, then Next EMI
      const deltaToTakeBack = value - previousChargeAmount;
      
      const currentForeclosureAmount = foreclosureCollection || 0;
      const currentPrepaymentAmount = prepaymentCollection || 0;
      const currentNextEmiAmount = advanceEmiCollection || 0;
      
      let remainingToTakeBack = deltaToTakeBack;
      
      // PRIORITY 1: Take from Foreclosure FIRST (includes both net foreclosure + foreclosure charges)
      // When taking back from foreclosure, we need to take BOTH the net amount AND its 4% charges
      const currentForeclosureCharges = foreclosureCharges || 0;
      const totalForeclosureWithCharges = currentForeclosureAmount + currentForeclosureCharges;
      
      if (totalForeclosureWithCharges > 0) {
        const amountFromForeclosureTotal = Math.min(remainingToTakeBack, totalForeclosureWithCharges);
        remainingToTakeBack = remainingToTakeBack - amountFromForeclosureTotal;
        
        // Calculate how much to reduce from net foreclosure (96%) and charges (4%)
        const netForeclosureToReduce = Math.ceil((amountFromForeclosureTotal * 100) / 104);
        const foreclosureChargesToReduce = amountFromForeclosureTotal - netForeclosureToReduce;
        
        // Reduce Foreclosure
        const newForeclosureAmount = Math.max(0, currentForeclosureAmount - netForeclosureToReduce);
        const newForeclosureCharges = Math.max(0, currentForeclosureCharges - foreclosureChargesToReduce);
        
        if (newForeclosureAmount > 0) {
          setForeclosureCollection(newForeclosureAmount);
          setForeclosureDisplay(newForeclosureAmount > 0 ? newForeclosureAmount.toFixed(2).replace(/\.00$/, '') : '');
          setForeclosureCharges(newForeclosureCharges);
          setNetForeclosureAmount(newForeclosureAmount);
          setShowForeclosureCharges(true);
        } else {
          // No foreclosure remaining, reset charges
          setForeclosureCollection(0);
          setForeclosureDisplay('');
          setForeclosureCharges(0);
          setNetForeclosureAmount(0);
          setShowForeclosureCharges(false);
        }
      }
      
      // PRIORITY 2: Take from Prepayment if still needed
      if (remainingToTakeBack > 0 && currentPrepaymentAmount > 0) {
        const amountFromPrepayment = Math.min(remainingToTakeBack, currentPrepaymentAmount);
        remainingToTakeBack = remainingToTakeBack - amountFromPrepayment;
        
        // Reduce Prepayment
        const newPrepaymentAmount = currentPrepaymentAmount - amountFromPrepayment;
        setPrepaymentCollection(newPrepaymentAmount);
        setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
      }
      
      // PRIORITY 3: Take from Next EMI if still needed
      if (remainingToTakeBack > 0 && currentNextEmiAmount > 0) {
        const amountFromNextEmi = Math.min(remainingToTakeBack, currentNextEmiAmount);
        
        const newNextEmiAmount = currentNextEmiAmount - amountFromNextEmi;
        setAdvanceEmiCollection(newNextEmiAmount);
        setAdvanceEmiDisplay(newNextEmiAmount > 0 ? newNextEmiAmount.toString() : '');
      }
    }
    
    // Reset flags after a short delay
    setTimeout(() => {
      setIsUpdatingFromManualFields(false);
      isManualUpdateRef.current = false;
      isChargeTransferRef.current = false;
    }, 50);
  };

  const handleChargeCollectionFocus = (e) => {
    if (!chargeCollection || chargeCollection === 0) {
      e.target.select();
    }
    if (chargeCollection === 0) {
      setChargeCollectionDisplay('');
    } else {
      const roundedValue = Math.round(chargeCollection);
      setChargeCollectionDisplay(roundedValue.toString());
    }
  };

  const handleChargeCollectionBlur = (e) => {
    const originalChargeAmount = chargeCollection;
    const maxCharge = remainingDemand.charges + standardEmi;
    
    // Validate and limit the amount on blur
    if (originalChargeAmount > maxCharge) {
      const excess = originalChargeAmount - maxCharge;
      setChargeCollection(maxCharge);
      setChargeCollectionDisplay(formatCurrency(maxCharge));
      showDebouncedToastError(`Charge collection limited to due amount of ₹${formatCurrency(maxCharge)}`);
      
      // Transfer the excess to Next EMI/Prepayment/Foreclosure
      const remainingNextEmiCapacity = remainingDemand.nextEmi;
      const currentNextEmiAmount = advanceEmiCollection || 0;
      const nextEmiRoom = Math.max(0, remainingNextEmiCapacity - currentNextEmiAmount);
      
      if (nextEmiRoom > 0) {
        const amountToNextEmi = Math.min(excess, nextEmiRoom);
        const remainingAfterNextEmi = excess - amountToNextEmi;
        
        const newNextEmiAmount = currentNextEmiAmount + amountToNextEmi;
        setAdvanceEmiCollection(newNextEmiAmount);
        setAdvanceEmiDisplay(newNextEmiAmount > 0 ? newNextEmiAmount.toString() : '');
        setManuallyEditedFields(prev => new Set(prev).add('nextEmi'));
        setAutoAllocatedFields(prev => new Set(prev).add('nextEmi'));
        
        if (amountToNextEmi > 0) {
          isNextEmiFromTransferRef.current = true;
        }
        
        // Check if remaining amount fits in prepayment or goes to foreclosure
        if (remainingAfterNextEmi > 0) {
          const currentPrepaymentAmount = prepaymentCollection || 0;
          const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
          
          if (prepaymentRoom > 0) {
            const amountToPrepayment = Math.min(remainingAfterNextEmi, prepaymentRoom);
            const amountToForeclosure = remainingAfterNextEmi - amountToPrepayment;
            
            // Allocate to prepayment
            if (amountToPrepayment > 0) {
              const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
              setPrepaymentCollection(newPrepaymentAmount);
              setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
              setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
              setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
            }
            
            // Allocate overflow to foreclosure
            if (amountToForeclosure > 0) {
              // When transferring from charges, split: 96% to foreclosure, 4% to charges
              const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
              const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
              
              const currentForeclosureAmount = foreclosureCollection || 0;
              const currentForeclosureCharges = foreclosureCharges || 0;
              
              const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
              const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
              
              setForeclosureCollection(newNetForeclosure);
              setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
              setForeclosureCharges(newForeclosureCharges);
              setNetForeclosureAmount(newNetForeclosure);
              setShowForeclosureCharges(true);
            }
          } else {
            // Prepayment is full, all goes to foreclosure
            // When transferring from charges, split: 96% to foreclosure, 4% to charges
            const netForeclosureToAdd = Math.ceil((remainingAfterNextEmi * 100) / 104);
            const foreclosureChargesToAdd = remainingAfterNextEmi - netForeclosureToAdd;
            
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
          }
        }
      } else {
        // No Next EMI room, check prepayment capacity
        const currentPrepaymentAmount = prepaymentCollection || 0;
        const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
        
        if (prepaymentRoom > 0) {
          const amountToPrepayment = Math.min(excess, prepaymentRoom);
          const amountToForeclosure = excess - amountToPrepayment;
          
          // Allocate to prepayment
          if (amountToPrepayment > 0) {
            const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
            setPrepaymentCollection(newPrepaymentAmount);
            setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
            setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
            setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
          }
          
          // Allocate overflow to foreclosure
          if (amountToForeclosure > 0) {
            // When transferring from charges, split: 96% to foreclosure, 4% to charges
            const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
            const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
            
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
          }
        } else {
          // Both Next EMI and Prepayment are full, all goes to foreclosure
          // When transferring from charges, split: 96% to foreclosure, 4% to charges
          const netForeclosureToAdd = Math.ceil((excess * 100) / 104);
          const foreclosureChargesToAdd = excess - netForeclosureToAdd;
          
          const currentForeclosureAmount = foreclosureCollection || 0;
          const currentForeclosureCharges = foreclosureCharges || 0;
          
          const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
          const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
          
          setForeclosureCollection(newNetForeclosure);
          setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
          setForeclosureCharges(newForeclosureCharges);
          setNetForeclosureAmount(newNetForeclosure);
          setShowForeclosureCharges(true);
        }
      }
    } else if (chargeCollection > 0) {
      const roundedValue = Math.round(chargeCollection);
      setChargeCollectionDisplay(formatCurrency(roundedValue));
    } else {
      setChargeCollectionDisplay('');
    }
  };

  // Tooltip toggle functions
  const togglePrepaymentTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowForeclosureTooltip(false);
    setShowPrepaymentTooltip(!showPrepaymentTooltip);
  };

  const toggleForeclosureTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPrepaymentTooltip(false);
    setShowForeclosureTooltip(!showForeclosureTooltip);
  };

  // Effect to handle automatic allocation when total collection changes
  useEffect(() => {
    // Skip if this update came from manual field changes or charge transfer to prevent loops
    if (isUpdatingFromManualFields || isManualUpdateRef.current || isChargeTransferRef.current) {
      const timeoutId = setTimeout(() => {
        setIsUpdatingFromManualFields(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }

    // Reset fields when total is 0
    if (totalCollection === 0) {
      // Reset all collections when total is 0, but preserve manually edited fields
      setOdCollection(0);
      setCurrentCollection(0);

      if (!manuallyEditedFields.has('charges')) {
        setChargeCollection(0);
        setChargeCollectionDisplay('');
      }

      if (!manuallyEditedFields.has('nextEmi')) {
        setAdvanceEmiCollection(0);
        setAdvanceEmiDisplay('');
      }

      if (!manuallyEditedFields.has('prepayment')) {
        setPrepaymentCollection(0);
        setPrepaymentDisplay('');
      }

      setForeclosureCollection(0);
      setHasShownOverflowMessage(false);
      setForeclosureCharges(0);
      setNetForeclosureAmount(0);
      setShowForeclosureCharges(false);
    }
  }, [totalCollection, isUpdatingFromManualFields, manuallyEditedFields]);

  // Don't render anything until initialized (prevents flash of search section)
  if (!isInitialized) {
    return null;
  }

  return (
    <>
      <div 
        className="min-vh-100 bg-light d-flex align-items-center justify-content-center p-0 p-md-4 modal-scrollable payment-modal-container"
        onContextMenu={(e) => {
          if (isLoadedFromURLParam) {
            e.preventDefault();
            return false;
          }
        }}
      >
        <div className="card payment-card w-100 modal-scrollable payment-modal-card">
          <div className="card-body p-2 p-md-4">
            {/* Header */}
            {/* <div className="d-flex align-items-center justify-content-between mb-4">
              <h3 className="h5 mb-0 fw-bold">Payment Portal</h3>
              <button className="btn-close" onClick={onClose} aria-label="Close">×</button>
            </div> */}

            {/* Search Modes */}
            {!isLoadedFromURLParam && (
            <div className="mb-1 mb-md-4">
              <div className="d-flex gap-2 gap-md-3 justify-content-around">
                {/* Loan ID */}
                <div 
                  onClick={() => {
                    if (isLoadedFromURLParam) return; // Disable click when loaded from URL
                    setSearchMode('loanid');
                    setSearchInput('');
                    setSearchError('');
                    setItems(null);
                    setShowDataCard(false);
                    setTotalCollection(0);
                    setTotalCollectionDisplay('');
                    setIsSearchInputDisabled(false);
                    resetCollectionFields();
                    handleModeChange({ target: { value: 'Loan ID' } });
                  }}
                  className={`search-mode-tab ${searchMode === 'loanid' ? 'active' : ''} ${isLoadedFromURLParam ? 'disabled' : ''}`}
                  style={isLoadedFromURLParam ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <div className="search-mode-icon-wrapper">
                    <img src={require(searchMode === 'loanid' ? "../../assets/images/loan-id-highlight.svg" : "../../assets/images/loan-id.svg")} alt="Loan ID" className="search-mode-icon" />
                  </div>
                  <div className={`search-mode-label ${searchMode === 'loanid' ? 'active' : 'inactive'}`}>
                    Loan ID
                  </div>
                </div>

                {/* Mobile Number */}
                <div 
                  onClick={() => {
                    if (isLoadedFromURLParam) return; // Disable click when loaded from URL
                    setSearchMode('mobile');
                    setSearchInput('');
                    setSearchError('');
                    setItems(null);
                    setShowDataCard(false);
                    setTotalCollection(0);
                    setTotalCollectionDisplay('');
                    setIsSearchInputDisabled(false);
                    resetCollectionFields();
                    handleModeChange({ target: { value: 'Mobile Number' } });
                  }}
                  className={`search-mode-tab ${searchMode === 'mobile' ? 'active' : ''} ${isLoadedFromURLParam ? 'disabled' : ''}`}
                  style={isLoadedFromURLParam ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <div className="search-mode-icon-wrapper">
                    <img src={require(searchMode === 'mobile' ? "../../assets/images/mobile-no-highlight.svg" : "../../assets/images/mobile-no.svg")} alt="Mobile" className="search-mode-icon" />
                  </div>
                  <div className={`search-mode-label ${searchMode === 'mobile' ? 'active' : 'inactive'}`}>
                    {isDesktop ? 'Mobile No' : 'Mobile'}
                  </div>
                </div>

                {/* Aadhaar */}
                <div 
                  onClick={() => {
                    if (isLoadedFromURLParam) return; // Disable click when loaded from URL
                    setSearchMode('aadhaar');
                    setSearchInput('');
                    setSearchError('');
                    setItems(null);
                    setShowDataCard(false);
                    setTotalCollection(0);
                    setTotalCollectionDisplay('');
                    setIsSearchInputDisabled(false);
                    resetCollectionFields();
                    handleModeChange({ target: { value: 'Aadhaar' } });
                  }}
                  className={`search-mode-tab ${searchMode === 'aadhaar' ? 'active' : ''} ${isLoadedFromURLParam ? 'disabled' : ''}`}
                  style={isLoadedFromURLParam ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <div className="search-mode-icon-wrapper">
                    <img src={require("../../assets/images/aadhaar-uidai.svg")} alt="Aadhaar" className="search-mode-icon" />
                  </div>
                  <div className={`search-mode-label ${searchMode === 'aadhaar' ? 'active' : 'inactive'}`}>
                    Aadhaar
                  </div>
                </div>

                {/* PAN Card */}
                <div 
                  onClick={() => {
                    if (isLoadedFromURLParam) return; // Disable click when loaded from URL
                    setSearchMode('pan');
                    setSearchInput('');
                    setSearchError('');
                    setItems(null);
                    setShowDataCard(false);
                    setTotalCollection(0);
                    setTotalCollectionDisplay('');
                    setIsSearchInputDisabled(false);
                    resetCollectionFields();
                    handleModeChange({ target: { value: 'PAN Card' } });
                  }}
                  className={`search-mode-tab ${searchMode === 'pan' ? 'active' : ''} ${isLoadedFromURLParam ? 'disabled' : ''}`}
                  style={isLoadedFromURLParam ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <div className="search-mode-icon-wrapper">
                    <img src={require("../../assets/images/pan-card.svg")} alt="PAN" className="search-mode-icon" />
                  </div>
                  <div className={`search-mode-label ${searchMode === 'pan' ? 'active' : 'inactive'}`}>
                    {isDesktop ? 'PAN Card' : 'PAN'}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Search Input and Button */}
            {!isLoadedFromURLParam && (
            <div className="mb-3 mb-md-4">
              <div className="row g-2 align-items-start">
                <div className="col-8 col-md-9">
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    value={searchInput}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Real-time validation based on search mode
                      if (searchMode === 'loanid') {
                        // Loan ID: Allow only alphanumeric, convert to uppercase
                        value = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                        // Limit to 16 characters (format: MP001LE000232N01)
                        value = value.slice(0, 16);
                      } else if (searchMode === 'mobile') {
                        // Mobile: Remove country codes, spaces, special characters
                        value = value.replace(/^\+91/, '').replace(/[^\d]/g, '').trim();
                        // Limit to 10 digits
                        value = value.slice(0, 10);
                      } else if (searchMode === 'aadhaar') {
                        // Aadhaar: Allow only digits
                        value = value.replace(/[^\d]/g, '');
                        // Limit to 12 digits
                        value = value.slice(0, 12);
                      } else if (searchMode === 'pan') {
                        // PAN: Allow only alphanumeric, convert to uppercase
                        value = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                        // Limit to 10 characters (format: ABCDE1234F)
                        value = value.slice(0, 10);
                      }
                      
                      setSearchInput(value);
                      setSearchError('');
                    }}
                    placeholder={getValidationMessage()}
                    disabled={isSearching || isSearchInputDisabled}
                  />
                  {searchError && <small className="text-danger d-block">{searchError}</small>}
                </div>
                <div className="col-4 col-md-3">
                  <button 
                    className="btn btn-primary w-100 fw-bold search-button"
                    onClick={handleSearch}
                    disabled={isSearching || !searchInput || isLoadedFromURLParam || isSearchInputDisabled}
                  >
                    {isSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>
            </div>
            )}

            {/* User Details Card */}
            {showDataCard && items && (
              <>
                {/* Warning Message - Only show if NOT loaded from URL parameter */}
                {!isLoadedFromURLParam && (
                  <div className="alert alert-danger d-flex align-items-center mb-3 warning-alert" role="alert">
                    <div className="mr-2 warning-alert-icon">ⓘ</div>
                    <div className="d-none d-md-block warning-alert-text warning-alert-text-desktop">
                      Please check details below before making payment. Kindly edit in Search if not correct.
                    </div>
                    <div className="d-md-none warning-alert-text warning-alert-text-mobile">
                      Please check details below before making payment. Kindly edit in Search if not correct.
                    </div>
                  </div>
                )}

                {/* User Info Card */}
                <div className="card mb-3 user-info-card">
                  <div className="card-body p-3 p-md-4">
                    {/* Desktop View - Two Rows */}
                    <div className="d-none d-md-block">
                      {/* Row 1: User Avatar and Name */}
                      <div className="d-flex align-items-center mb-3">
                        <div className="text-white d-flex align-items-center justify-content-center user-avatar-wrapper-desktop mr-2">
                            <img src={require("../../assets/images/default-user.svg")} alt="user" className="user-avatar-icon" />
                        </div>
                        <div className="ms-3 text-left">
                          <small className="text-muted user-label text-left">Borrower</small>
                          <h5 className="mb-0 fw-bold user-name-desktop">{items.Lead_Name || 'N/A'}</h5>
                        </div>
                      </div>

                      {/* Row 2: Details Grid */}
                      <div className="row g-4">
                        <div className="col-4">
                          <div className="d-flex align-items-center">
                              <div className="text-info mr-2 user-detail-icon-wrapper">
                                <img src={require("../../assets/images/user-loan-id.svg")} alt="loan id" className="user-detail-icon-sm" />
                              </div>
                            <div>
                              <small className="text-muted d-block user-detail-label text-left">Loan ID</small>
                              <strong className="user-detail-value-desktop">{items.HH_ID || 'N/A'}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="d-flex align-items-center">
                            <div className="text-info mr-2 user-detail-icon-wrapper">
                              <img src={require("../../assets/images/user-mobile-no.svg")} alt="phone" className="user-detail-icon-sm" />
                            </div>
                            <div>
                              <small className="text-muted d-block user-detail-label text-left">Phone</small>
                              <strong className="user-detail-value-desktop">{items.Household_mobile_no || 'N/A'}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="d-flex align-items-center">
                            <div className="text-info mr-2 user-detail-icon-wrapper">
                              <img src={require("../../assets/images/user-rupee.svg")} alt="rupee" className="user-detail-icon-xs" />
                            </div>
                            <div>
                              <small className="text-muted d-block user-detail-label text-left">Loan Amount</small>
                              <strong className="text-dark user-detail-amount-desktop">₹{formatCurrency(items.disbursement_amt ? parseFloat(items.disbursement_amt) : (items.Balance || 0))}/-</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Mobile View - Original Layout */}
                    <div className="d-md-none">
                      {/* User Avatar and Name */}
                      <div className="d-flex align-items-start mb-3">
                        <div className="text-white d-flex align-items-center justify-content-center mr-2 user-avatar-wrapper-mobile">
                          <img src={require("../../assets/images/default-user.svg")} alt="user" className="user-avatar-icon-mobile" />
                        </div>
                        <div className="ms-3 d-flex flex-column justify-content-center">
                          <small className="text-muted text-left">Borrower</small>
                          <h5 className="mb-0 fw-bold user-name-mobile">{items.Lead_Name || 'N/A'}</h5>
                        </div>
                      </div>

                      {/* Details Grid */}
                      <div className="row g-3">
                        <div className="col-6 mb-2">
                          <div className="d-flex align-items-start">
                            <div className="text-info mr-2 user-detail-icon-wrapper-mobile">
                              <img src={require("../../assets/images/user-loan-id.svg")} alt="loan id" className="user-detail-icon-xs" />
                            </div>
                            <div>
                              <small className="text-muted d-block user-detail-label text-left">Loan ID</small>
                              <strong className="user-detail-value-mobile">{items.HH_ID || 'N/A'}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="d-flex align-items-start">
                            <div className="text-info mr-2 user-detail-icon-wrapper-mobile">
                              <img src={require("../../assets/images/user-mobile-no.svg")} alt="phone" className="user-detail-icon-xs" />
                            </div>
                            <div>
                              <small className="text-muted d-block user-detail-label text-left">Phone</small>
                              <strong className="user-detail-value-mobile">{items.Household_mobile_no || 'N/A'}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex align-items-start">
                            <div className={`text-info ${isDesktop ? 'mr-2' : 'mr-1'} user-detail-icon-wrapper-mobile`}>
                              <img src={require("../../assets/images/user-rupee.svg")} alt="rupee" className="user-detail-icon-xs" />
                            </div>
                            <div>
                              <small className="text-muted d-block user-detail-label text-left">Loan Amount</small>
                              <strong className="text-dark user-detail-amount-mobile">₹{formatCurrency(items.disbursement_amt ? parseFloat(items.disbursement_amt) : (items.Balance || 0))}/-</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Edit and Confirm Buttons */}
                {!isLoadedFromURLParam && (
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <button 
                      className="btn btn-outline-info w-100 py-2 fw-medium d-flex align-items-center justify-content-center gap-2 action-button"
                      onClick={() => {
                        setIsSearchInputDisabled(false);
                        setIsCollectionConfirmed(false);
                      }}
                      disabled={isSubmitting}
                    >
                      <img src={require("../../assets/images/edit-pen.svg")} alt="Edit" className="edit-button-icon" />Edit
                    </button>
                  </div>
                  <div className="col-6">
                    <button 
                      className="btn btn-info text-white w-100 py-2 fw-medium action-button confirm-button"
                      onClick={() => {
                        // If no amount entered, auto-populate with total demand
                        if (!totalCollection || totalCollection === 0) {
                          const autoTotal = remainingDemand.total;
                          setTotalCollection(autoTotal);
                          setTotalCollectionDisplay(autoTotal.toString());
                          // Run allocation with auto-populated amount
                          autoAllocateCollection(autoTotal);
                        } else {
                          // Amount already entered, just run allocation
                          autoAllocateCollection(totalCollection);
                        }
                        setIsCollectionConfirmed(true);
                      }}
                      disabled={!showDataCard}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
                )}

                {/* Collection Summary Table - Desktop View */}
                <div className="d-none d-md-block mb-3 mb-md-4">
                  <table className="table table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th className="text-center fw-bold collection-table-header"></th>
                        <th className="text-center fw-bold collection-table-header">Due</th>
                        <th className="text-center fw-bold collection-table-header">Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Amount */}
                      <tr className="collection-table-row-total">
                        <td className="text-center fw-bold collection-table-cell-total">Amount</td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" className="form-control form-control-sm text-center fw-bold collection-table-input-total" disabled 
                            value={formatCurrency(remainingDemand.total)} 
                          />
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input 
                            type="text"
                            id="totalCollectionTable"
                            name="totalCollectionTable"
                            className="form-control form-control-sm text-center fw-bold" 
                            value={totalCollectionDisplay || ''}
                            onChange={handleTotalCollectionChange}
                            onFocus={handleTotalCollectionFocus}
                            onBlur={handleTotalCollectionBlur}
                            placeholder="Enter amount"
                            disabled={isLoadedFromURLParam || isCollectionConfirmed}
                            style={{fontSize: '0.85rem', color: '#000'}}
                          />
                        </td>
                      </tr>
                      
                      {/* Overdue */}
                      <tr className="overdue-row">
                        <td className="text-center fw-semibold collection-table-cell">Overdue</td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" className="form-control form-control-sm text-center collection-table-input" disabled 
                            value={formatCurrency(remainingDemand.od)} 
                          />
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value" 
                            value={formatCurrency(odCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Current EMI */}
                      <tr className="current-emi-row">
                        <td className="text-center fw-semibold collection-table-cell">Current EMI</td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" className="form-control form-control-sm text-center collection-table-input" disabled 
                            value={formatCurrency(remainingDemand.current)} 
                          />
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value" 
                            value={formatCurrency(currentCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Charges */}
                      <tr className="charges-row">
                        <td className="text-center fw-semibold collection-table-cell">Charges</td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" className="form-control form-control-sm text-center collection-table-input" disabled 
                            value={formatCurrency(remainingDemand.charges)} 
                          />
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value" 
                            value={chargeCollectionDisplay || ''}
                            onChange={handleChargeCollectionChange}
                            onFocus={handleChargeCollectionFocus}
                            onBlur={handleChargeCollectionBlur}
                            placeholder="0"
                            style={{backgroundColor: isLoadedFromURLParam ? 'transparent' : undefined, border: isLoadedFromURLParam ? 'none' : undefined}}
                          />
                        </td>
                      </tr>

                      {/* Next EMI */}
                      <tr className="next-emi-row">
                        <td className="text-center fw-semibold collection-table-cell">Next EMI</td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" className="form-control form-control-sm text-center collection-table-input" disabled 
                            value={formatCurrency(remainingDemand.nextEmi)} 
                          />
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value" 
                            value={formatCurrency(advanceEmiCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Prepayment */}
                      <tr className="prepayment-row">
                        <td className="text-center fw-semibold collection-table-cell">
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <span>Advance</span>&nbsp;
                            <div className="info-icon-wrapper">
                              <span 
                                onClick={togglePrepaymentTooltip}
                                className="info-icon"
                              >
                                ⓘ
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" className="form-control form-control-sm text-center collection-table-input" disabled 
                            value={formatCurrency(maxPrepaymentAmount)} 
                          />
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value" 
                            value={formatCurrency(prepaymentCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Foreclosure */}
                      <tr className="foreclosure-row">
                        <td className="text-center fw-semibold collection-table-cell">
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <span>Foreclosure</span>&nbsp;
                            <span 
                              className="d-inline-flex align-items-center justify-content-center info-icon-foreclosure"
                              onClick={toggleForeclosureTooltip}
                            >
                              ⓘ
                            </span>
                          </div>
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center collection-table-input" 
                            style={{height: '38px'}}>-</span>
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value" 
                            value={formatCurrency(foreclosureCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Foreclosure Charges */}
                      {showForeclosureCharges && foreclosureCharges > 0 && (
                        <tr className="foreclosure-charges-row">
                          <td className="text-center fw-semibold collection-table-cell">
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <span>Foreclosure Charges</span>
                              <span className="foreclosure-charge-label">(4%)</span>
                            </div>
                          </td>
                          <td className="text-center" style={{verticalAlign: 'middle'}}>
                            <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center collection-table-input" 
                              style={{height: '38px'}}>-</span>
                          </td>
                          <td className="text-center" style={{verticalAlign: 'middle'}}>
                            <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value collection-table-input-disabled" 
                              value={foreclosureCharges > 0 ? foreclosureCharges.toFixed(2).replace(/\.?0+$/, '') : ''}
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Prepayment Tooltip Message - Desktop */}
                {showPrepaymentTooltip && (
                  <div className="tooltip-message d-none d-md-block pb-2">
                    <span>Up to {maxPrepaymentEmis} EMI allowed</span>
                  </div>
                )}
                
                {/* Foreclosure Tooltip Message - Desktop */}
                {showForeclosureTooltip && (
                  <div className="tooltip-message d-none d-md-block pb-2">
                    <span>For Foreclosure contact TO at branch.</span>
                  </div>
                )}

                {/* Collection Summary Table - Mobile View */}
                <div className="d-md-none mb-3 mb-md-4">
                  <table className="table table-bordered table-sm">
                    <thead className="table-light">
                      <tr>
                        <th className="text-center fw-bold collection-table-header-mobile" style={{width: '40%'}}></th>
                        <th className="text-center fw-bold collection-table-header-mobile" style={{width: '30%'}}>Due</th>
                        <th className="text-center fw-bold collection-table-header-mobile" style={{width: '30%'}}>Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Amount */}
                      <tr className="collection-table-row-total">
                        <td className="text-center fw-bold collection-table-cell-mobile" style={{color: '#000'}}>Amount</td>
                        <td className="text-center collection-table-cell-mobile">
                          <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center fw-bold collection-table-input-mobile">
                            {formatCurrency(remainingDemand.total)}
                          </span>
                        </td>
                        <td className="text-center collection-table-cell-mobile">
                          <input 
                            type="text"
                            id="totalCollectionTableMobile"
                            name="totalCollectionTableMobile"
                            className="form-control form-control-sm text-center fw-bold collection-table-input-total-mobile" 
                            value={totalCollectionDisplay || ''}
                            onChange={handleTotalCollectionChange}
                            onFocus={handleTotalCollectionFocus}
                            onBlur={handleTotalCollectionBlur}
                            placeholder="Amount"
                            disabled={isLoadedFromURLParam || isCollectionConfirmed}
                          />
                        </td>
                      </tr>
                      
                      {/* Overdue */}
                      <tr className="overdue-row">
                        <td className="text-center fw-semibold collection-table-cell-mobile">Overdue</td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" className="form-control form-control-sm text-center collection-table-input-value-mobile" disabled 
                            value={formatCurrency(remainingDemand.od)} 
                            style={{backgroundColor: 'transparent', border: 'none'}}
                          />
                        </td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value-mobile" 
                            value={formatCurrency(odCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Current EMI */}
                      <tr className="current-emi-row">
                        <td className="text-center fw-semibold collection-table-cell-mobile">Current EMI</td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" className="form-control form-control-sm text-center collection-table-input-value-mobile" disabled 
                            value={formatCurrency(remainingDemand.current)} 
                            style={{backgroundColor: 'transparent', border: 'none'}}
                          />
                        </td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value-mobile" 
                            value={formatCurrency(currentCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Charges */}
                      <tr className="charges-row">
                        <td className="text-center fw-semibold collection-table-cell-mobile">Charges</td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" className="form-control form-control-sm text-center collection-table-input-value-mobile" disabled 
                            value={formatCurrency(remainingDemand.charges)} 
                            style={{backgroundColor: 'transparent', border: 'none'}}
                          />
                        </td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value-mobile" 
                            value={chargeCollectionDisplay || ''}
                            onChange={handleChargeCollectionChange}
                            onFocus={handleChargeCollectionFocus}
                            onBlur={handleChargeCollectionBlur}
                            placeholder="0"
                            style={{backgroundColor: isLoadedFromURLParam ? 'transparent' : undefined, border: isLoadedFromURLParam ? 'none' : undefined}}
                          />
                        </td>
                      </tr>

                      {/* Next EMI */}
                      <tr className="next-emi-row">
                        <td className="text-center fw-semibold collection-table-cell-mobile">Next EMI</td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" className="form-control form-control-sm text-center collection-table-input-value-mobile" disabled 
                            value={formatCurrency(remainingDemand.nextEmi)} 
                            style={{backgroundColor: 'transparent', border: 'none'}}
                          />
                        </td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value-mobile" 
                            value={formatCurrency(advanceEmiCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Prepayment */}
                      <tr className="prepayment-row">
                        <td className="text-center fw-semibold collection-table-cell-mobile">
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <span>Advance</span>&nbsp;
                            <div className="info-icon-wrapper">
                              <span 
                                onClick={togglePrepaymentTooltip}
                                className="info-icon-mobile"
                              >
                                ⓘ
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" className="form-control form-control-sm text-center collection-table-input-value-mobile" disabled 
                            value={formatCurrency(maxPrepaymentAmount)} 
                            style={{backgroundColor: 'transparent', border: 'none'}}
                          />
                        </td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value-mobile" 
                            value={formatCurrency(prepaymentCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Foreclosure */}
                      <tr className="foreclosure-row">
                        <td className="text-center fw-semibold collection-table-cell-mobile">
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <span>Foreclosure</span>&nbsp;
                            <span 
                              className="d-inline-flex align-items-center justify-content-center info-icon-mobile"
                              onClick={toggleForeclosureTooltip}
                            >
                              ⓘ
                            </span>
                          </div>
                        </td>
                        <td className="text-center collection-table-cell-mobile">
                          <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center collection-table-input-value-mobile" 
                            style={{backgroundColor: 'transparent', border: 'none', height: '30px'}}>-</span>
                        </td>
                        <td className="text-center collection-table-cell-mobile">
                          <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value-mobile" 
                            value={formatCurrency(foreclosureCollection)}
                          />
                        </td>
                      </tr>
                      
                      {/* Foreclosure Charges */}
                      {showForeclosureCharges && foreclosureCharges > 0 && (
                        <tr className="foreclosure-charges-row">
                          <td className="text-center fw-semibold collection-table-cell-mobile">
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <span>Foreclosure Charges</span>
                              <span className="foreclosure-charge-label-mobile">(4%)</span>
                            </div>
                          </td>
                          <td className="text-center collection-table-cell-mobile">
                            <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center collection-table-input-value-mobile" 
                              style={{backgroundColor: 'transparent', border: 'none', height: '30px'}}>-</span>
                          </td>
                          <td className="text-center collection-table-cell-mobile">
                            <input type="text" disabled className="form-control form-control-sm text-center collection-table-input-value-mobile collection-table-input-disabled" 
                              value={foreclosureCharges > 0 ? foreclosureCharges.toFixed(2).replace(/\.?0+$/, '') : ''}
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Prepayment Tooltip Message - Mobile */}
                {showPrepaymentTooltip && (
                  <div className="tooltip-message-mobile d-md-none pb-2">
                    <span>Up to {maxPrepaymentEmis} EMI allowed</span>
                  </div>
                )}
                
                {/* Foreclosure Tooltip Message - Mobile */}
                {showForeclosureTooltip && (
                  <div className="tooltip-message-mobile d-md-none pb-2">
                    <span>For Foreclosure contact TO at branch.</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="row g-2">
                  {!isLoadedFromURLParam && (
                  <div className="col-6">
                    <button 
                      className="btn btn-outline-info w-100 py-2 fw-medium d-flex align-items-center justify-content-center gap-2 action-button"
                      onClick={() => {
                        setIsCollectionConfirmed(false);
                      }}
                      disabled={isSubmitting}
                    >
                      Change Amount
                    </button>
                  </div>
                  )}
                  <div className={isLoadedFromURLParam ? "col-12" : "col-6"}>
                    <button 
                      className="btn text-white w-100 py-2 fw-medium d-flex align-items-center justify-content-center gap-2 pay-button"
                      onClick={handleDirectSubmit}
                      disabled={isSubmitting || !totalCollection || totalCollection <= 0}
                    >
                      <img src={PayButtonIcon} alt="Pay" className="pay-button-icon mr-2" />
                      {isSubmitting ? 'Processing...' : 'Pay'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal fade show d-block success-modal-overlay" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body p-4 text-center">
                <div className="mb-3">
                  <div className="success-modal-icon">✓</div>
                </div>
                <h4 className="fw-bold mb-3">Payment Successful!</h4>
                {items && (
                  <>
                    <p className="mb-2">Name: <strong>{items.Lead_Name || 'N/A'}</strong></p>
                    {searchMode === 'loanid' && (
                      <p className="mb-2">Loan ID: <strong>{items.HH_ID || 'N/A'}</strong></p>
                    )}
                    {searchMode === 'mobile' && (
                      <p className="mb-2">Mobile: <strong>{items.Household_mobile_no || 'N/A'}</strong></p>
                    )}
                    {searchMode === 'aadhaar' && (
                      <p className="mb-2">Aadhaar: <strong>{items.aadhaar_no || 'N/A'}</strong></p>
                    )}
                    {searchMode === 'pan' && (
                      <p className="mb-2">PAN: <strong>{items.pan_no || 'N/A'}</strong></p>
                    )}
                  </>
                )}
                <p className="mb-2">Payment ID: <strong>{paymentId}</strong></p>
                <p className="mb-4">Amount: <strong>₹{formatCurrency(totalCollection)}</strong></p>
                <button 
                  className="btn btn-primary px-4 search-button"
                  onClick={async () => {
                    setShowSuccessModal(false);
                    // Fetch updated demand after payment
                    await fetchUpdatedDemand();
                    // Only call handleClear if NOT loaded from URL parameter
                    if (!isLoadedFromURLParam) {
                      handleClear();
                    }
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="modal fade show d-block confirmation-modal-overlay" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered mx-2 mx-md-auto">
            <div className="modal-content confirmation-modal-content">
              <div className="modal-header border-0 pb-0 justify-content-center position-relative">
                <h5 className="modal-title fw-bold text-dark">Confirm Payment</h5>
                <button 
                  type="button" 
                  className="btn-close position-absolute confirmation-modal-close" 
                  onClick={handleConfirmationCancel}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="modal-body text-center p-3">
                <h5 className="mb-4 text-dark">
                  Do you want to proceed with <strong>{confirmationMode}</strong>?
                </h5>
                <p className="mb-4 text-muted">
                  Amount: <strong>₹{formatCurrency(totalCollection)}</strong>
                </p>

                <div className="d-flex justify-content-center" style={{gap: '20px'}}>
                  <button 
                    type="button" 
                    className="btn btn-secondary px-5 py-2 action-button"
                    onClick={handleConfirmationCancel}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary px-5 py-2 search-button"
                    onClick={handleConfirmedSubmit}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="modal fade show d-block confirmation-modal-overlay" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered mx-2 mx-md-auto">
            <div className="modal-content confirmation-modal-content">
              <div className="modal-header border-0 pb-0 justify-content-center position-relative">
                <h5 className="modal-title fw-bold text-dark">Confirm Payment</h5>
                <button 
                  type="button" 
                  className="btn-close position-absolute confirmation-modal-close" 
                  onClick={handleConfirmationCancel}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="modal-body text-center p-3">
                <h5 className="mb-4 text-dark">
                  Do you want to proceed with <strong>{confirmationMode}</strong>?
                </h5>
                <p className="mb-4 text-muted">
                  Amount: <strong>₹{formatCurrency(totalCollection)}</strong>
                </p>

                <div className="d-flex justify-content-center" style={{gap: '20px'}}>
                  <button 
                    type="button" 
                    className="btn btn-secondary px-5 py-2 action-button"
                    onClick={handleConfirmationCancel}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary px-5 py-2 search-button"
                    onClick={handleConfirmedSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : 'OK'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            style: {
              background: '#4caf50',
            },
          },
          error: {
            style: {
              background: '#f44336',
            },
          },
        }}
      />
    </>
  );
}
